import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ImportService } from '../import.service';
import { DataService } from '../data.service';
import { ProjectService, ProjectDocument } from '../project.service';
import { ZeichnungenService } from '../zeichnungen.service';
import { BereicheService } from '../bereiche.service';
import {
  TransformationService,
  ImportValidationResult,
  ImportValidationSummary,
  formatValidationForClipboard,
  copyToClipboard,
  clampImportedWorkflow,
  initialWorkflow,
  restartWorkflow,
  materializeLegacyBestand,
  legacyRegionsToBereichInputs,
  ConsoleErrorService,
} from '@luzula/luzula-lib';
import { AuthService } from '../auth.service';
import { AufnahmePunkt, AufnahmePunktDocument, BodenformPair, punktartFromSymbol, punktTemplate, CURRENT_VERSION, LocationSystem, istPunktartMitProtokoll, ImportInfo, BodenvegetationZeile } from '../data-model';
import { parseFeinbodenform, parseHbfList } from './bodenform-parser';
import { convertOnImport } from '../bohrpunkte/bestand/bestand-parser';
import { foreignProjectOf } from '../bohrpunkte/detail/point-project-membership';
import { LatLng } from 'leaflet';
import { ladeXlsx } from './xlsx-laden';

type ImportStep = 'select' | 'select-layer' | 'analyzing' | 'preview' | 'importing' | 'complete';
type WizardMode = 'import' | 'patch-vegetation' | 'restore';

/** Default-Strategie fuer den Restore-Preview-Step (global). */
export type RestoreStrategy = 'overwrite-and-new' | 'only-new';

/**
 * Strategie fuer die Projekt-DATEN (Bereiche/Zeichnungen/Layer) beim Restore in
 * ein BESTEHENDES Projekt (User-Wahl 2026-06-09):
 *  - `replace`: Backup-Stand ersetzt den DB-Stand (vollstaendige Wiederherstellung).
 *  - `merge`: nicht-destruktiv — Zeichnungen werden additiv vereint (kein Feature
 *    geht verloren), die uebrigen Kategorien bleiben DB-seitig erhalten, sofern
 *    vorhanden, und werden nur aus dem Backup ergaenzt, wo die DB leer ist.
 */
export type ProjectDataStrategy = 'replace' | 'merge';

/**
 * Permission-Gates fuers Projekt-Daten-Restore — gegen das ZIEL-Projekt
 * ausgewertet (Entscheidung 2026-06-09):
 *  - `canDrawingsAndLayers` (= `canEditDrawings`): gatet Zeichnungen UND geladene
 *    Layer (`mapDefinition`, Layer-State, Geodaten).
 *  - `canRegions` (= `canEditRegions`): gatet Bereiche.
 */
export interface RestoreDataGates {
	canDrawingsAndLayers: boolean;
	canRegions: boolean;
}

/** Projekt-Doc-Keys der Kategorie „Bereiche" (Gate: canEditRegions). */
const RESTORE_REGION_KEYS = ['regions', 'projectPoints'];
/** Projekt-Doc-Keys der Kategorien „Zeichnungen" + „geladene Layer" (Gate: canEditDrawings). */
const RESTORE_DRAWING_LAYER_KEYS = [
	'zeichnungslayer', 'zeichnungslayer_version',
	'mapDefinition', 'layerOrder', 'hiddenLayers',
	'pointsLayerVisible', 'polygonsLayerVisible',
	'maxZoomLevel', 'offlinePlusLevels', 'dgm_calibration',
	'geodataFiles', 'attachmentsMeta'
];

/** True, wenn ein Restore-Wert „leer" ist (fuer den nicht-destruktiven Merge). */
function isEmptyRestoreValue(v: any): boolean {
	if (v === undefined || v === null) return true;
	if (Array.isArray(v)) return v.length === 0;
	if (typeof v === 'object' && Array.isArray(v.regions)) return v.regions.length === 0;
	return false;
}

/**
 * Additive Vereinigung zweier Zeichnungslayer-Arrays (Layer nach `id`, Features
 * nach `properties._uid`) — gespiegelt aus der Sync-Merge-Logik der Lib, damit
 * der „merge"-Restore keine Zeichnung verliert.
 */
function unionZeichnungslayer(dbArr: any, backupArr: any): any {
	if (!Array.isArray(dbArr)) return backupArr;
	if (!Array.isArray(backupArr)) return dbArr;
	const byId = new Map<string, any>();
	for (const layer of dbArr) {
		byId.set(layer.id, { ...layer, geojson: { ...layer.geojson, features: [...(layer.geojson?.features ?? [])] } });
	}
	for (const bl of backupArr) {
		const existing = byId.get(bl.id);
		if (!existing) {
			byId.set(bl.id, { ...bl, geojson: { ...bl.geojson, features: [...(bl.geojson?.features ?? [])] } });
			continue;
		}
		const seen = new Set(existing.geojson.features.map((f: any) => f?.properties?._uid).filter(Boolean));
		for (const f of bl.geojson?.features ?? []) {
			const uid = f?.properties?._uid;
			if (uid && seen.has(uid)) continue;
			existing.geojson.features.push(f);
			if (uid) seen.add(uid);
		}
	}
	return Array.from(byId.values());
}

/**
 * Wendet eine einzelne Kategorie-Key auf das Ziel-Doc an (strategie-abhaengig).
 * `replace`: Backup gewinnt (wenn vorhanden). `merge`: Zeichnungen additiv,
 * `zeichnungslayer_version` als max+1, sonst DB behalten und nur fuellen, wo leer.
 */
function applyRestoreKey(doc: any, dbProj: any, backupProj: any, key: string, strategy: ProjectDataStrategy): void {
	if (!(key in backupProj)) return; // Backup kennt den Key nicht → DB-Wert bleibt
	if (strategy === 'replace') {
		doc[key] = backupProj[key];
		return;
	}
	// merge
	if (key === 'zeichnungslayer') {
		doc[key] = unionZeichnungslayer(dbProj[key], backupProj[key]);
		return;
	}
	if (key === 'zeichnungslayer_version') {
		doc[key] = Math.max(Number(dbProj[key] ?? 0), Number(backupProj[key] ?? 0)) + 1;
		return;
	}
	if (isEmptyRestoreValue(dbProj[key])) doc[key] = backupProj[key];
}

/**
 * Baut das beim Restore zu schreibende Projekt-Doc — permission-gegated und
 * strategie-abhaengig. Pure (kein DI) → gut testbar.
 *
 * - Neues Projekt (kein `dbProj`): kompletter Backup-Stand, User wird Owner +
 *   Bearbeiter (volle Rechte → alle Kategorien). DB-Member-Felder leer.
 * - Bestehendes Projekt: DB-Stand als Basis; Metadaten (name/description) sowie
 *   die ERLAUBTEN Kategorien werden gemaess Strategie uebernommen. Owner/Member/
 *   workflow/archived/pendingInvites bleiben strikt DB-seitig (Restore eskaliert
 *   keine Projekt-Rechte). Verbotene Kategorien bleiben unveraendert.
 */
export function mergeRestoredProjectDoc(
	backupProj: any,
	dbProj: any | null,
	opts: { currentUserId: string; currentUser: string; gates: RestoreDataGates; strategy: ProjectDataStrategy }
): { doc: any; restored: string[]; skipped: string[] } {
	if (!dbProj) {
		const owner = backupProj.owner ?? { id: opts.currentUserId, username: opts.currentUser };
		const doc: any = { ...backupProj, owner, bearbeiter: [owner], kontrolleure: [], archived: false, pendingInvites: [] };
		// PLAN-bereiche-db.md P5: Flag eines migrierten QUELL-Projekts nicht
		// uebernehmen — sonst ueberspringt die P4-Auto-Migration beim ersten
		// Oeffnen das Seeden der Bereiche-DB und die Bereiche waeren unsichtbar.
		delete doc.bereicheMigrated;
		return {
			doc,
			restored: ['Bereiche', 'Zeichnungen', 'Layer'],
			skipped: []
		};
	}

	const doc: any = { ...dbProj, name: backupProj.name, description: backupProj.description };
	const restored: string[] = [];
	const skipped: string[] = [];

	if (opts.gates.canRegions) {
		for (const k of RESTORE_REGION_KEYS) applyRestoreKey(doc, dbProj, backupProj, k, opts.strategy);
		restored.push('Bereiche');
	} else {
		skipped.push('Bereiche');
	}

	if (opts.gates.canDrawingsAndLayers) {
		for (const k of RESTORE_DRAWING_LAYER_KEYS) applyRestoreKey(doc, dbProj, backupProj, k, opts.strategy);
		restored.push('Zeichnungen', 'Layer');
	} else {
		skipped.push('Zeichnungen', 'Layer');
	}

	// DB-Felder bleiben strikt erhalten (Restore eskaliert keine Projekt-Rechte).
	doc._id = dbProj._id;
	doc._rev = dbProj._rev;
	doc.owner = dbProj.owner;
	doc.bearbeiter = dbProj.bearbeiter;
	doc.kontrolleure = dbProj.kontrolleure;
	doc.workflow = dbProj.workflow;
	doc.archived = dbProj.archived;
	doc.pendingInvites = dbProj.pendingInvites;
	return { doc, restored, skipped };
}

/**
 * Schreibt die beim Restore wiederhergestellten Zeichnungs-Features in die
 * dedizierte Zeichnungen-DB (S2). Nötig, weil der Loader die Features aus dem
 * Store liest, nicht aus dem Doc-Array. Neues Projekt → migrateFromArray
 * (Geo-Dedupe); bestehendes → saveProject (der gemergte Soll-Zustand).
 * Gibt true zurück, wenn geseedet wurde (Caller setzt dann zeichnungenMigrated).
 */
/**
 * Schreibt die beim Restore wiederhergestellten Bereiche in die dedizierte
 * Bereiche-DB (PLAN-bereiche-db.md P5) — NUR wenn das ZIEL-Projekt migriert
 * ist; sonst lesen LayerService & Co. die Doc-Keys und die P4-Auto-Migration
 * uebernimmt beim ersten Oeffnen. `replace`: Store leeren + Backup-Stand rein.
 * `merge`: Store (Ziel) behaelt Vorrang — nur befuellen, wo leer (Spiegel der
 * applyRestoreKey-Semantik). Gibt true zurueck, wenn der Store zustaendig war.
 */
export async function seedRestoredBereiche(
	bereiche: {
		listProject(id: string): Promise<any[]>;
		bulkInsert(id: string, inputs: any[]): Promise<number>;
		removeProject(id: string): Promise<void>;
	},
	projectId: string,
	mergedDoc: any,
	strategy: ProjectDataStrategy,
	isMigratedTarget: boolean
): Promise<boolean> {
	if (!isMigratedTarget) return false;
	const inputs = legacyRegionsToBereichInputs(
		mergedDoc?.regions,
		mergedDoc?.projectPoints?.regionNames,
		mergedDoc?.projectPoints?.regionFilters
	);
	if (strategy === 'replace') {
		await bereiche.removeProject(projectId);
		if (inputs.length) await bereiche.bulkInsert(projectId, inputs);
		return true;
	}
	const existing = await bereiche.listProject(projectId);
	if (existing.length === 0 && inputs.length) await bereiche.bulkInsert(projectId, inputs);
	return true;
}

export async function seedRestoredDrawings(
	zeichnungen: {
		openProject(id: string): Promise<void>;
		migrateFromArray(id: string, layers: any[]): Promise<unknown>;
		saveProject(id: string, layers: any[], stand?: undefined, optionen?: { ebenenEntfernen?: boolean }): Promise<void>;
	},
	projectId: string,
	layers: any[] | undefined | null,
	isNewProject: boolean,
	strategy?: ProjectDataStrategy
): Promise<boolean> {
	if (!Array.isArray(layers)) return false;
	await zeichnungen.openProject(projectId);
	if (isNewProject) {
		await zeichnungen.migrateFromArray(projectId, layers);
	} else if (strategy === 'replace') {
		// Ein gewöhnliches Speichern löscht seit 28.09.2026 keine ganzen Ebenen mehr
		// (Vorfall Frenzel_Mankmoos). Beim Ersetzen soll der Stand der Sicherung
		// gelten: Ebenen, die sie nicht kennt, fallen ausdrücklich weg.
		await zeichnungen.saveProject(projectId, layers, undefined, { ebenenEntfernen: true });
	} else {
		await zeichnungen.saveProject(projectId, layers);
	}
	return true;
}

/**
 * Status eines einzelnen Punkts in der Restore-Preview-Liste:
 *  - `new`: existiert nicht in der Ziel-DB → wird angelegt
 *  - `overwritable`: existiert + Permission OK → kann ueberschrieben werden
 *  - `blocked`: existiert + Permission FEHLT (Status × Rolle × Ownership)
 */
export type RestoreItemStatus = 'new' | 'overwritable' | 'blocked';

/**
 * Eine Zeile in der Restore-Preview. Selected steuert, ob der Punkt beim
 * Schreiben angefasst wird; bei `blocked` ist selected fix false.
 */
export interface RestoreItem {
	point: AufnahmePunktDocument;
	existing: AufnahmePunktDocument | null;
	status: RestoreItemStatus;
	selected: boolean;
	/** User-tauglicher Grund, warum dieser Punkt blockiert ist (nur bei `blocked`). */
	reason?: string;
}

/**
 * Ein Treffer für den „Vegetation nachtragen"-Modus: Excel-Sheet → bestehender DB-Doc.
 * Entsteht beim Analyze-Schritt; verbraucht beim Apply-Schritt.
 */
interface VegetationPatchMatch {
	filename: string;
	sheetName: string;
	source: string; // "datei.xlsx / Sheet1"
	nummer: string;
	rightVegetations: BodenvegetationZeile[];   // aus Excel, rechte Spalte + Bemerkungen
	candidates: AufnahmePunktDocument[];         // DB-Treffer (auf nummer)
	resolvedDoc?: AufnahmePunktDocument;          // eindeutiger Doc, sofern Match
	newEntries: BodenvegetationZeile[];           // die tatsächlich anzuhängenden (nach Dedup)
	cleanedBemerkungen?: string;                  // bereinigter Text, wenn anders als aktuell
	bemerkungenRemovedCount: number;              // Anzahl aus Bemerkungen extrahierter Einträge
	status: 'ready' | 'no-match' | 'multiple-matches' | 'no-changes' | 'error';
	statusMessage: string;
	applied?: boolean;
	error?: string;
}

interface GpkgLayerChoice {
  file: File;
  layers: string[];
  selectedLayer: string;
}

interface ParsedFile {
  filename: string;
  sheets: ParsedSheet[];
}

interface ParsedSheet {
  name: string;
  format: 'bohrpunkt' | 'moorpunkt' | 'unknown';
  validation: ImportValidationResult;
  data?: AufnahmePunkt;
  importInfo?: ImportInfo;  // Wird beim Import gesetzt
}

interface DuplicateInfo {
  newPoint: AufnahmePunkt;
  existingId: string;
  existingNummer?: string;
  matchType: 'koordinaten' | 'nummer' | 'datum_kartierer';
  distance?: number;
  /**
   * Name des Projekts, zu dem der GEFUNDENE Punkt gehoert — nur gesetzt, wenn das
   * ein ANDERES als das aktive Projekt ist. Solche Treffer werden nie
   * ueberschrieben (Cross-Projekt-Schutz, User-Entscheidung 2026-07-11): der
   * Duplikat-Abgleich laeuft datenbankweit, und ohne diese Sperre wuerde `update`
   * fremde Projektdaten zerschreiben und den Punkt ins eigene Projekt ziehen.
   * Projektlose Altpunkte gelten NICHT als fremd.
   */
  foreignProjectName?: string;
}

@Component({
  selector: 'bp-import-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbProgressbarModule],
  templateUrl: './import-wizard.component.html',
  styleUrls: ['./import-wizard.component.css']
})
export class ImportWizardComponent implements OnInit {
  step: ImportStep = 'select';

  // Modus: regulärer Import oder Vegetations-Nachtrag
  mode: WizardMode = 'import';

  // Dateiauswahl
  excelFiles: File[] = [];
  gpkgFiles: File[] = [];

  // Patch-Modus: Treffer-Liste
  patchMatches: VegetationPatchMatch[] = [];
  patchedCount = 0;
  patchSkippedCount = 0;

  // Patch-Modus: Projekt-Vorauswahl (leerer String = alle Projekte)
  patchProjectId: string = '';

  // === Restore-Modus (Projekt-Backup-Wiederherstellung) ===
  /** Projekt-Doc aus dem Backup (schon migriert auf CURRENT_VERSION). */
  restoreProject: ProjectDocument | null = null;
  /** Sammel-Warnungen aus applyBackupMigrations (datenverlust-Hinweise). */
  restoreMigrationWarnings: string[] = [];
  /** Pro Punkt: Status + Selektion + Permission-Hinweis. */
  restoreItems: RestoreItem[] = [];
  /** Globale Default-Strategie — wirkt auf den Default-Haken pro Zeile. */
  restoreStrategy: RestoreStrategy = 'overwrite-and-new';
  /** Strategie fuer die Projekt-DATEN (Bereiche/Zeichnungen/Layer) in ein bestehendes Projekt. */
  projectDataStrategy: ProjectDataStrategy = 'replace';
  /** Anzahl restaurierter Punkte (Anlegen + Ueberschreiben), fuer den Abschluss-Step. */
  restoredCount = 0;
  /** Kategorien, die beim Restore tatsaechlich geschrieben wurden (Abschluss-Step). */
  restoreDataRestored: string[] = [];
  /** Kategorien, die mangels Recht uebersprungen wurden (Abschluss-Step). */
  restoreDataSkipped: string[] = [];
  /**
   * Binäre Geodaten-Blobs aus dem Backup-ZIP (Dateiname → Bytes). Werden beim
   * Apply rollenabhängig als Projekt-Attachments re-attached (Phase 3 — Layer-
   * Restore). Leer bei v1-Backups / Projekten ohne Geodaten.
   */
  restoreAttachments: Record<string, Uint8Array> = {};

  // Analyse-Ergebnisse
  parsedFiles: ParsedFile[] = [];
  validationSummary: ImportValidationSummary | null = null;

  // Duplikate
  duplicates: DuplicateInfo[] = [];

  // Import-Optionen
  // duplicateAction: 'keep_both' entfaellt (User-Wunsch 2026-06-05 — fuehrte zu
  // unkontrollierten Duplikaten). 'update' ist permission-gated und greift nur
  // fuer Manager/Entwickler (siehe canIOverwriteOnImport + effectiveDuplicateAction).
  importOptions = {
    importValid: true,
    importWithWarnings: true,
    duplicateAction: 'skip' as 'skip' | 'update'
  };

  // Filter für Anzeige: 'all' | 'success' | 'warning' | 'error'
  displayFilter: 'all' | 'success' | 'warning' | 'error' = 'all';

  toggleFilter(filter: 'success' | 'warning' | 'error'): void {
    this.displayFilter = this.displayFilter === filter ? 'all' : filter;
  }

  sheetMatchesFilter(sheet: ParsedSheet): boolean {
    if (this.displayFilter === 'all') return true;
    if (this.displayFilter === 'error') return !sheet.validation.isValid;
    if (this.displayFilter === 'warning') return sheet.validation.isValid && sheet.validation.hasWarnings;
    if (this.displayFilter === 'success') return sheet.validation.isValid && !sheet.validation.hasWarnings;
    return true;
  }

  // Fortschritt
  progress = 0;
  progressText = '';

  // Ergebnis
  importedCount = 0;
  skippedCount = 0;

  // Cache für bestehende Punkte (für Duplikatprüfung)
  private existingPoints: AufnahmePunktDocument[] = [];

  // Layer-Auswahl für GeoPackage mit mehreren Punkt-Layern
  gpkgLayerChoices: GpkgLayerChoice[] = [];

  activeModal = inject(NgbActiveModal);
  private importService = inject(ImportService);
  private ces = inject(ConsoleErrorService);
  private dataService = inject(DataService);
  private transformService = inject(TransformationService);
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);
  projectService = inject(ProjectService);
  private zeichnungen = inject(ZeichnungenService);
  private bereiche = inject(BereicheService);

  /** Projektliste fürs Dropdown — wird im Template über patchProjects() gelesen */
  get patchProjects(): ProjectDocument[] {
    return this.projectService.projectList?.() ?? [];
  }

  /** Anzeigename des aktuell ausgewählten Patch-Projekts (oder „alle Projekte") */
  get patchProjectLabel(): string {
    if (!this.patchProjectId) return 'alle Projekte';
    const p = this.patchProjects.find(pr => pr._id === this.patchProjectId);
    return p?.name ?? this.patchProjectId;
  }

  ngOnInit(): void {}

  // === Dateiauswahl ===

  onExcelFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.excelFiles = Array.from(input.files);
    }
  }

  onGpkgFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.gpkgFiles = Array.from(input.files);
    }
  }

  removeExcelFile(index: number): void {
    this.excelFiles.splice(index, 1);
  }

  removeGpkgFile(index: number): void {
    this.gpkgFiles.splice(index, 1);
  }

  get hasFiles(): boolean {
    return this.excelFiles.length > 0 || this.gpkgFiles.length > 0;
  }

  // === Analyse ===

  async startAnalysis(): Promise<void> {
    // Patch-Modus: GPKG ist nicht relevant, kein Layer-Picker nötig
    if (this.mode === 'patch-vegetation') {
      await this.runPatchAnalysis();
      return;
    }

    // Zuerst prüfen, ob Layer-Auswahl für GeoPackage nötig ist
    this.gpkgLayerChoices = [];

    for (const file of this.gpkgFiles) {
      if (file.name.toLowerCase().endsWith('.gpkg')) {
        const pointLayers = await this.getGpkgPointLayers(file);
        if (pointLayers.length > 1) {
          // Mehrere Punkt-Layer: Benutzer muss wählen
          this.gpkgLayerChoices.push({
            file,
            layers: pointLayers,
            selectedLayer: pointLayers[0]
          });
        }
      }
    }

    // Falls Layer-Auswahl nötig, erst zur Auswahl gehen
    if (this.gpkgLayerChoices.length > 0) {
      this.step = 'select-layer';
      return;
    }

    // Sonst direkt analysieren
    await this.runAnalysis();
  }

  // === Patch-Modus: nur rechte Vegetations-Spalte in bestehende Docs nachtragen ===

  private async runPatchAnalysis(): Promise<void> {
    this.step = 'analyzing';
    this.progress = 0;
    this.patchMatches = [];

    // Bestand aus DB laden (für Nummer-Match)
    this.progressText = 'Lade bestehende Bohrpunkte...';
    try {
      const result = await this.dataService.queryIndex(true);
      this.existingPoints = result?.rows
        ? result.rows.map((row: any) => row.doc as AufnahmePunktDocument)
        : [];
    } catch (error) {
      console.error('Fehler beim Laden bestehender Punkte:', error);
      this.toastr.error('Bestehende Punkte konnten nicht geladen werden');
      this.step = 'select';
      return;
    }

    // Vorauswahl: nur Bohrpunkte des gewählten Projekts berücksichtigen.
    // Damit fallen alte Restbestände aus anderen Projekten (Reimport-Leichen)
    // beim Matching weg und das Token-Match wird eindeutig.
    if (this.patchProjectId) {
      const before = this.existingPoints.length;
      this.existingPoints = this.existingPoints.filter(
        d => d.header?.projects?.some(p => p.id === this.patchProjectId) ?? false
      );
      console.log(
        `Patch-Modus: ${before} Bohrpunkte → ${this.existingPoints.length} im Projekt "${this.patchProjectLabel}"`
      );
    }

    const total = this.excelFiles.length || 1;
    let processed = 0;

    for (const file of this.excelFiles) {
      this.progressText = `Analysiere ${file.name}...`;
      try {
        const xlsx = await ladeXlsx();
        const data = await file.arrayBuffer();
        const wb = xlsx.read(data, { cellDates: true });

        for (const sheetName of wb.SheetNames) {
          const ws = wb.Sheets[sheetName];
          const sheetData = xlsx.utils.sheet_to_json(ws, { blankrows: true, header: 'A' }) as any[];

          // Nur Bohrpunkt-Sheets sind betroffen (Moor hat ohnehin schon zwei Spalten)
          if (this.importService.detectSheetFormat(sheetData) !== 'bohrpunkt') continue;

          this.patchMatches.push(this.buildPatchMatch(file.name, sheetName, sheetData));
        }
      } catch (error: any) {
        this.patchMatches.push({
          filename: file.name,
          sheetName: '',
          source: file.name,
          nummer: '',
          rightVegetations: [],
          candidates: [],
          newEntries: [],
          bemerkungenRemovedCount: 0,
          status: 'error',
          statusMessage: `Fehler beim Lesen: ${error.message}`
        });
      }
      processed++;
      this.progress = Math.round((processed / total) * 100);
    }

    this.step = 'preview';
  }

  private buildPatchMatch(
    filename: string,
    sheetName: string,
    sheet: any[]
  ): VegetationPatchMatch {
    const source = `${filename} / ${sheetName}`;

    // Vollparse für Identifier (Nummer + Koordinaten als Tiebreaker)
    const parsed = this.importService.bohrpunktFromExcelSheet(sheet);
    const rightVegetations = this.importService.bohrpunktVegetationRightFromExcelSheet(sheet);

    if (!parsed) {
      return {
        filename, sheetName, source, nummer: '',
        rightVegetations, candidates: [], newEntries: [], bemerkungenRemovedCount: 0,
        status: 'error', statusMessage: 'Sheet konnte nicht gelesen werden'
      };
    }

    const nummer = String(parsed.header.nummer ?? '').trim();

    if (!nummer) {
      return {
        filename, sheetName, source, nummer,
        rightVegetations, candidates: [], newEntries: [], bemerkungenRemovedCount: 0,
        status: 'no-match', statusMessage: 'Bohrpunktnummer fehlt im Sheet'
      };
    }

    const { candidates, matchType } = this.importService.findNummerMatches(nummer, this.existingPoints);

    // Bei mehreren Treffern: Koordinaten als Tiebreaker (wenn vorhanden)
    let resolvedDoc: AufnahmePunktDocument | undefined;
    if (candidates.length === 1) {
      resolvedDoc = candidates[0];
    } else if (candidates.length > 1 && parsed.header.koordinaten?.x && parsed.header.koordinaten?.y) {
      const px = parsed.header.koordinaten.x;
      const py = parsed.header.koordinaten.y;
      const ranked = candidates
        .map(d => ({
          doc: d,
          dist: d.header?.koordinaten?.x && d.header?.koordinaten?.y
            ? Math.hypot(d.header.koordinaten.x - px, d.header.koordinaten.y - py)
            : Infinity
        }))
        .sort((a, b) => a.dist - b.dist);
      // eindeutig zuordnen, wenn der nächste Treffer < 5m und der zweitnächste >= 50m ist
      if (ranked[0].dist < 5 && (ranked[1]?.dist ?? Infinity) >= 50) {
        resolvedDoc = ranked[0].doc;
      }
    }

    if (!resolvedDoc) {
      if (candidates.length === 0) {
        return {
          filename, sheetName, source, nummer,
          rightVegetations, candidates, newEntries: [], bemerkungenRemovedCount: 0,
          status: 'no-match',
          statusMessage: `Kein Bohrpunkt mit Nummer "${nummer}" gefunden (auch nicht als Token in einer umbenannten Nummer)`
        };
      }
      const candidateLabels = candidates
        .map(d => `"${d.header?.nummer ?? '?'}"`)
        .join(', ');
      const matchLabel = matchType === 'exact' ? 'exakt' : matchType === 'token' ? 'als Token' : '';
      return {
        filename, sheetName, source, nummer,
        rightVegetations, candidates, newEntries: [], bemerkungenRemovedCount: 0,
        status: 'multiple-matches',
        statusMessage: `${candidates.length} Bohrpunkte gefunden (${matchLabel}: ${candidateLabels}) — Koordinaten reichen nicht zur Auflösung`
      };
    }

    // Bestehende Einträge des Docs für Dedup
    const existingVegs = resolvedDoc.bodenvegetation?.aufnahme?.vegetationen ?? [];
    const seen = new Set(existingVegs.map(v => `${v.vegetation}${String(v.haeufigkeit ?? '')}`));

    // Aktuellen Bemerkungstext des Docs parsen — extrahierte Einträge fließen
    // sowohl in newEntries (mit Dedup) als auch in cleanedBemerkungen (Strip).
    // Quelle ist bewusst der DOC, nicht der Excel-Snapshot, damit Edits seit
    // dem Erstimport respektiert werden (Prosa-Zusätze bleiben, manuell
    // entfernte Einträge werden nicht reanimiert).
    const currentBemerkungen = String(resolvedDoc.header?.bemerkungen ?? '');
    const docBemkParse = this.importService.parseBemerkungenVegetationen(
      currentBemerkungen ? currentBemerkungen.split('\n') : []
    );
    const cleanedBemerkungenText = docBemkParse.remainingLines.join('\n');
    const cleanedBemerkungen = cleanedBemerkungenText !== currentBemerkungen
      ? cleanedBemerkungenText
      : undefined;
    const bemerkungenRemovedCount = docBemkParse.vegetationen.length;

    // Kandidaten für neue Einträge: Excel-Quelle + aus aktuellen Bemerkungen
    const allCandidates = [...rightVegetations, ...docBemkParse.vegetationen];
    const newEntries: BodenvegetationZeile[] = [];
    for (const v of allCandidates) {
      const k = `${v.vegetation}${String(v.haeufigkeit ?? '')}`;
      if (seen.has(k)) continue;
      seen.add(k);
      newEntries.push(v);
    }

    if (newEntries.length === 0 && cleanedBemerkungen === undefined) {
      return {
        filename, sheetName, source, nummer,
        rightVegetations, candidates: [resolvedDoc], resolvedDoc, newEntries,
        bemerkungenRemovedCount,
        status: 'no-changes', statusMessage: 'Keine Änderungen nötig'
      };
    }

    const parts: string[] = [];
    if (newEntries.length > 0) parts.push(`${newEntries.length} Eintrag/Einträge anhängen`);
    if (cleanedBemerkungen !== undefined) {
      parts.push(`${bemerkungenRemovedCount} aus Bemerkungen extrahieren`);
    }

    return {
      filename, sheetName, source, nummer,
      rightVegetations, candidates: [resolvedDoc], resolvedDoc, newEntries,
      cleanedBemerkungen, bemerkungenRemovedCount,
      status: 'ready', statusMessage: parts.join(', ')
    };
  }

  get patchReadyCount(): number {
    return this.patchMatches.filter(m => m.status === 'ready').length;
  }
  get patchEntriesCount(): number {
    return this.patchMatches.reduce((sum, m) => sum + (m.status === 'ready' ? m.newEntries.length : 0), 0);
  }
  get patchBemerkungenCleanCount(): number {
    return this.patchMatches.filter(m => m.status === 'ready' && m.cleanedBemerkungen !== undefined).length;
  }
  get patchBemerkungenRemovedTotal(): number {
    return this.patchMatches.reduce(
      (sum, m) => sum + (m.status === 'ready' && m.cleanedBemerkungen !== undefined ? m.bemerkungenRemovedCount : 0),
      0
    );
  }
  get patchNoMatchCount(): number {
    return this.patchMatches.filter(m => m.status === 'no-match').length;
  }
  get patchMultipleCount(): number {
    return this.patchMatches.filter(m => m.status === 'multiple-matches').length;
  }
  get patchNoChangesCount(): number {
    return this.patchMatches.filter(m => m.status === 'no-changes').length;
  }
  get patchErrorCount(): number {
    return this.patchMatches.filter(m => m.status === 'error').length;
  }

  async startPatchApply(): Promise<void> {
    this.step = 'importing';
    this.progress = 0;
    this.patchedCount = 0;
    this.patchSkippedCount = 0;

    const allReady = this.patchMatches.filter(m => m.status === 'ready' && m.resolvedDoc);
    // Rechtematrix B2: Der Patch ändert BESTEHENDE Punkte (Vegetation nachtragen) —
    // also pro Punkt nur, wenn der User ihn auch einzeln editieren dürfte
    // (`canIWritePoint`: eigener Punkt in `bearbeitung` bzw. Owner/Entwickler, nicht
    // eingefroren). Sonst könnte ein Bearbeiter via Patch-Import fremde/eingefrorene/
    // projektfremde Punkte ändern. Gesperrte werden übersprungen + gemeldet.
    const ready = allReady.filter(m => this.projectService.canIWritePoint(m.resolvedDoc as any));
    const blockedByPermission = allReady.length - ready.length;
    if (blockedByPermission > 0) {
      this.patchSkippedCount += blockedByPermission;
      allReady
        .filter(m => !this.projectService.canIWritePoint(m.resolvedDoc as any))
        .forEach(m => { m.status = 'error'; m.error = 'keine Schreibrechte (fremder/eingefrorener Punkt)'; });
    }
    if (ready.length === 0) {
      if (blockedByPermission > 0) {
        this.toastr.warning(`${blockedByPermission} Punkte übersprungen — keine Schreibrechte`);
      }
      this.step = 'complete';
      return;
    }

    // WICHTIG: alle Patches in EINEM bulkDocs-Call. Pro-Doc-saveDocumentQuiet
    // würde 2 Round-Trips (get + put) × N Bohrpunkte erzeugen — das hat im
    // Live-Sync gegen den Remote-Server bereits eine IP-Sperre ausgelöst.
    this.progressText = `Sende ${ready.length} Patches gebündelt...`;
    const updatedDocs: any[] = ready.map(match => {
      const target = match.resolvedDoc!;
      const updated: any = {
        ...target,
        bodenvegetation: {
          ...(target.bodenvegetation ?? { aufnahme: { schichtAnteile: {}, vegetationen: [] } }),
          aufnahme: {
            ...(target.bodenvegetation?.aufnahme ?? { schichtAnteile: {}, vegetationen: [] }),
            vegetationen: [
              ...(target.bodenvegetation?.aufnahme?.vegetationen ?? []),
              ...match.newEntries
            ]
          }
        }
      };
      // Bemerkungen nur überschreiben, wenn der Parser dort Vegetations-Einträge
      // gefunden und entfernt hat. Sonst lassen wir das Feld unangetastet, um
      // unbedacht keine User-Edits zu verlieren.
      if (match.cleanedBemerkungen !== undefined) {
        updated.header = {
          ...(target.header ?? {}),
          bemerkungen: match.cleanedBemerkungen
        };
      }
      return updated;
    });

    try {
      await this.dataService.bulkDocs(updatedDocs);
      ready.forEach(m => { m.applied = true; });
      this.patchedCount = ready.length;
      this.progress = 100;
    } catch (error: any) {
      const msg = error?.message ?? String(error);
      ready.forEach(m => { m.applied = false; m.error = msg; });
      this.patchSkippedCount = ready.length;
      console.error('Patch-bulkDocs fehlgeschlagen:', error);
    }

    if (this.patchedCount > 0) {
      this.toastr.success(`${this.patchedCount} Bohrpunkte um Vegetation ergänzt`);
    }
    if (this.patchSkippedCount > 0) {
      this.toastr.warning(`${this.patchSkippedCount} Punkte nicht ergänzt (keine Schreibrechte oder Fehler) — Details im Protokoll`);
    }
    this.step = 'complete';
  }

  async continueAfterLayerSelection(): Promise<void> {
    await this.runAnalysis();
  }

  private async runAnalysis(): Promise<void> {
    this.step = 'analyzing';
    this.progress = 0;
    this.parsedFiles = [];

    // Shapefile-Companions zaehlen nicht als eigene "Datei" in der Fortschrittsanzeige
    const primaryGpkgFiles = this.gpkgFiles.filter(f => !this.isShapefileCompanion(f));
    const totalFiles = this.excelFiles.length + primaryGpkgFiles.length;
    let processedFiles = 0;

    // Excel-Dateien verarbeiten
    for (const file of this.excelFiles) {
      this.progressText = `Analysiere ${file.name}...`;
      try {
        const parsedFile = await this.parseExcelFile(file);
        this.parsedFiles.push(parsedFile);
      } catch (error: any) {
        this.parsedFiles.push({
          filename: file.name,
          sheets: [{
            name: file.name,
            format: 'unknown',
            validation: {
              source: file.name,
              isValid: false,
              hasWarnings: false,
              messages: [{
                severity: 'error',
                field: 'Datei',
                value: '',
                message: `Fehler beim Lesen: ${error.message}`
              }]
            }
          }]
        });
      }
      processedFiles++;
      this.progress = Math.round((processedFiles / totalFiles) * 100);
    }

    // GPKG/GeoJSON/Shapefile-Dateien verarbeiten
    // Shapefile-Begleitdateien (.dbf/.prj/.shx/.cpg) werden uebersprungen — die
    // zugehoerige .shp zieht sie sich aus this.gpkgFiles ueber den Basisnamen.
    for (const file of this.gpkgFiles) {
      if (this.isShapefileCompanion(file)) continue;
      this.progressText = `Analysiere ${file.name}...`;
      try {
        // Layer-Auswahl berücksichtigen
        const layerChoice = this.gpkgLayerChoices.find(c => c.file === file);
        const selectedLayer = layerChoice?.selectedLayer;
        const parsedFile = await this.parseGeoFile(file, selectedLayer);
        this.parsedFiles.push(parsedFile);
      } catch (error: any) {
        this.parsedFiles.push({
          filename: file.name,
          sheets: [{
            name: file.name,
            format: 'unknown',
            validation: {
              source: file.name,
              isValid: false,
              hasWarnings: false,
              messages: [{
                severity: 'error',
                field: 'Datei',
                value: '',
                message: `Fehler beim Lesen: ${error.message}`
              }]
            }
          }]
        });
      }
      processedFiles++;
      this.progress = Math.round((processedFiles / totalFiles) * 100);
    }

    // Zusammenfassung erstellen
    this.createValidationSummary();

    // Duplikate prüfen
    await this.checkDuplicates();

    this.step = 'preview';
  }

  private async parseExcelFile(file: File): Promise<ParsedFile> {
    const xlsx = await ladeXlsx();
    const data = await file.arrayBuffer();
    const wb = xlsx.read(data, { cellDates: true });

    const sheets: ParsedSheet[] = [];

    for (const sheetName of wb.SheetNames) {
      const ws = wb.Sheets[sheetName];
      const sheetData = xlsx.utils.sheet_to_json(ws, { blankrows: true, header: 'A' }) as any[];

      const format = this.importService.detectSheetFormat(sheetData);
      if (format === 'unknown') continue;
      const validation: ImportValidationResult = {
        source: `${file.name} / ${sheetName}`,
        isValid: true,
        hasWarnings: false,
        messages: []
      };

      let parsedData: AufnahmePunkt | undefined;

      try {
        if (format === 'bohrpunkt') {
          parsedData = this.importService.bohrpunktFromExcelSheet(sheetData);

          // Info über Koordinaten-Transformation anzeigen
          const parseInfo = this.importService.lastParseInfo;
          if (parseInfo.coordTransform) {
            validation.messages.push({
              severity: 'info',
              field: 'Koordinaten',
              value: '',
              message: `Transformation: ${parseInfo.coordTransform}`
            });
          }
          if (parseInfo.pointType) {
            validation.messages.push({
              severity: 'info',
              field: 'Punkttyp',
              value: '',
              message: parseInfo.pointType
            });
          }
          // Anmerkung: fehlendes Datum wird jetzt von bohrpunktFromExcelSheet als Soft-Error
          // behandelt (datum=''); validateParsedData faengt das ab und vermerkt eine Warnung.
        } else if (format === 'moorpunkt') {
          parsedData = this.importService.moorpunktFromExcelSheet(sheetData);
        }

        if (parsedData) {
          // Basis-Validierung
          this.validateParsedData(parsedData, validation);
        }
      } catch (error: any) {
        validation.isValid = false;
        validation.messages.push({
          severity: 'error',
          field: 'Parsing',
          value: '',
          message: error.message
        });
      }

      validation.data = parsedData;

      // Import-Info für späteres Speichern vorbereiten
      const parseInfo = this.importService.lastParseInfo;
      const importInfo: ImportInfo = {
        time: Date.now(),
        user: this.authService.currentUser || undefined,
        source: `${file.name} / ${sheetName}`,
        coordTransform: parseInfo.coordTransform
      };

      sheets.push({
        name: sheetName,
        format,
        validation,
        data: parsedData,
        importInfo
      });
    }

    return { filename: file.name, sheets };
  }

  private async parseGeoFile(file: File, selectedLayer?: string): Promise<ParsedFile> {
    const fileName = file.name.toLowerCase();
    let features: any[] = [];

    if (fileName.endsWith('.gpkg')) {
      features = await this.parseGeoPackage(file, selectedLayer);
    } else if (fileName.endsWith('.geojson') || fileName.endsWith('.json')) {
      features = await this.parseGeoJSON(file);
    } else if (fileName.endsWith('.shp')) {
      features = await this.parseShapefile(file);
    } else {
      throw new Error('Nicht unterstütztes Dateiformat');
    }

    // Features zu Punkten konvertieren
    const sheets: ParsedSheet[] = [];
    let pointIndex = 0;

    for (const feature of features) {
      pointIndex++;

      const validation: ImportValidationResult = {
        source: `${file.name} / Feature ${pointIndex}`,
        isValid: true,
        hasWarnings: false,
        messages: []
      };

      try {
        const parsedData = this.convertFeatureToPoint(feature);
        if (parsedData) {
          this.validateParsedData(parsedData, validation);
          validation.data = parsedData;

          sheets.push({
            name: `Feature ${pointIndex}`,
            format: 'bohrpunkt', // GeoJSON/GPKG werden als generische Punkte behandelt
            validation,
            data: parsedData
          });
        }
      } catch (error: any) {
        validation.isValid = false;
        validation.messages.push({
          severity: 'error',
          field: 'Konvertierung',
          value: '',
          message: error.message
        });
        sheets.push({
          name: `Feature ${pointIndex}`,
          format: 'unknown',
          validation
        });
      }
    }

    return { filename: file.name, sheets };
  }

  private async parseGeoJSON(file: File): Promise<any[]> {
    const text = await file.text();
    const geojson = JSON.parse(text);

    if (geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
      throw new Error('Ungültiges GeoJSON: FeatureCollection erwartet');
    }

    // Nur Punkt-Features zurückgeben
    return geojson.features.filter((f: any) =>
      f.geometry && f.geometry.type === 'Point'
    );
  }

  /**
   * Liest eine Shapefile-Geometrie (.shp) zusammen mit ihren Begleitdateien (.dbf, .prj,
   * .cpg) aus der aktuellen Auswahl `this.gpkgFiles`. Voraussetzung: Begleitdateien
   * tragen denselben Basisnamen.
   *
   * Eigenschaften (Properties) erwarten wir analog zum GeoPackage-Pfad — `HBF`, `BF`,
   * `HF`, `SYMBOL`, `SYM_TEXT`, `Nummer`, `Bemerkungen`. Die Bodenform-Logik in
   * `convertFeatureToPoint` ist identisch.
   *
   * Mit vorhandener `.prj` projiziert shpjs nach WGS84; ohne `.prj` kommen die rohen
   * Quell-Koordinaten durch — die Heuristik in `convertFeatureToPoint` (UTM/EPSG:5650)
   * faengt das ab.
   */
  private async parseShapefile(file: File): Promise<any[]> {
    const baseName = file.name.replace(/\.shp$/i, '');
    const baseLower = baseName.toLowerCase();

    const findCompanion = (ext: string): File | undefined =>
      this.gpkgFiles.find(f => f.name.toLowerCase() === `${baseLower}.${ext}`);

    const dbfFile = findCompanion('dbf');
    if (!dbfFile) {
      throw new Error(`Shapefile braucht ${baseName}.dbf — bitte zusammen mit der .shp hochladen`);
    }
    const prjFile = findCompanion('prj');
    const cpgFile = findCompanion('cpg');

    const [shpBuffer, dbfBuffer, prjString, cpgBuffer] = await Promise.all([
      file.arrayBuffer(),
      dbfFile.arrayBuffer(),
      prjFile ? prjFile.text() : Promise.resolve<string | undefined>(undefined),
      cpgFile ? cpgFile.arrayBuffer() : Promise.resolve<ArrayBuffer | undefined>(undefined),
    ]);

    const shpjs = await import('shpjs');
    const geometries = shpjs.parseShp(shpBuffer, prjString);
    const attributes = shpjs.parseDbf(dbfBuffer, cpgBuffer);
    const fc = shpjs.combine([geometries, attributes]) as { features: any[] };

    return fc.features.filter(f =>
      f.geometry && (f.geometry.type === 'Point' || f.geometry.type === 'MultiPoint')
    );
  }

  /**
   * True, wenn die Datei eine Shapefile-Begleitdatei ist (.dbf/.prj/.shx/.cpg).
   * Die wird beim Iterieren ueber gpkgFiles uebersprungen — die zugehoerige .shp
   * holt sie sich beim Parsen ueber `findCompanion()`.
   */
  private isShapefileCompanion(file: File): boolean {
    return /\.(dbf|prj|shx|cpg)$/i.test(file.name);
  }

  /**
   * Ermittelt alle Punkt-Layer in einem GeoPackage
   */
  private async getGpkgPointLayers(file: File): Promise<string[]> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const { GeoPackageAPI } = await import('@ngageoint/geopackage');
    const geoPackage = await GeoPackageAPI.open(uint8Array);

    const featureTables = geoPackage.getFeatureTables();
    const pointLayers: string[] = [];

    console.log('GeoPackage Feature-Tabellen:', featureTables);

    for (const tableName of featureTables) {
      const featureDao = geoPackage.getFeatureDao(tableName);

      // Geometrie-Typ aus der FeatureDao-Definition prüfen
      const geomColType = (featureDao as any).geometryType || (featureDao as any).table?.geometryType;
      console.log(`Layer "${tableName}": geometryType = "${geomColType}"`);

      // Punkt-Layer erkennen (case-insensitive)
      const geomTypeStr = String(geomColType || '').toUpperCase();
      const isPointLayer = geomTypeStr === 'POINT' || geomTypeStr === 'MULTIPOINT' || geomTypeStr.includes('POINT');

      if (isPointLayer) {
        pointLayers.push(tableName);
        console.log(`  -> Punkt-Layer erkannt!`);
      } else {
        // Fallback: Erste Zeile prüfen via GeoJSON-Konvertierung
        let hasPoints = false;
        const rows = featureDao.queryForAll();
        if (rows.length > 0) {
          const firstRow = rows[0] as any;
          const geom = firstRow['geometry'] || firstRow.geometry;
          if (geom && typeof geom.toGeoJSON === 'function') {
            const geojson = geom.toGeoJSON();
            console.log(`  -> Erste Geometrie: type="${geojson?.type}"`);
            if (geojson?.type === 'Point' || geojson?.type === 'MultiPoint') {
              hasPoints = true;
              pointLayers.push(tableName);
              console.log(`  -> Punkt-Layer erkannt (via GeoJSON)!`);
            }
          } else {
            console.log(`  -> Keine Geometrie oder toGeoJSON nicht verfügbar`);
          }
        }
      }
    }

    return pointLayers;
  }

  private async parseGeoPackage(file: File, selectedLayer?: string): Promise<any[]> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Dynamic import für GeoPackage API
    const { GeoPackageAPI } = await import('@ngageoint/geopackage');
    const geoPackage = await GeoPackageAPI.open(uint8Array);

    const featureTables = geoPackage.getFeatureTables();
    if (featureTables.length === 0) {
      throw new Error('Keine Feature-Tabellen im GeoPackage gefunden');
    }

    // Layer bestimmen: explizit gewählt, oder automatisch
    let tableName: string;
    if (selectedLayer) {
      tableName = selectedLayer;
    } else {
      // Automatische Auswahl: Punkt-Layer finden
      const pointLayers = await this.getGpkgPointLayers(file);
      if (pointLayers.length === 0) {
        // Fallback: Alle Feature-Tabellen anzeigen
        console.warn('Keine Punkt-Layer gefunden, verfügbare Tabellen:', featureTables);
        throw new Error(`Keine Punkt-Layer im GeoPackage gefunden. Verfügbare Layer: ${featureTables.join(', ')}`);
      }
      // Bevorzuge Layer mit "punkt" im Namen, sonst den ersten
      tableName = pointLayers.find(t => t.toLowerCase().includes('punkt')) || pointLayers[0];
    }

    console.log(`Verwende Layer: "${tableName}"`);

    // SRS (Koordinatensystem) des Layers ermitteln
    const featureDao = geoPackage.getFeatureDao(tableName);
    const srs = featureDao.srs;
    const epsgCode = srs?.organization_coordsys_id;
    console.log('Layer SRS:', srs?.srs_name, 'EPSG:', epsgCode);

    // Verwende iterateGeoJSONFeatures für korrekte GeoJSON-Konvertierung
    const features: any[] = [];

    // Diese Methode liefert direkt GeoJSON-Features mit korrekter Geometrie
    const featureIterator = geoPackage.iterateGeoJSONFeatures(tableName);
    for (const feature of featureIterator) {
      if (feature.geometry &&
          (feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint')) {
        // EPSG-Code hinzufügen für spätere Transformation
        (feature as any).crs = epsgCode;
        features.push(feature);
      }
    }

    console.log(`${features.length} Punkt-Features gefunden`);
    if (features.length > 0) {
      console.log('Beispiel-Feature:', features[0]);
      console.log('Beispiel-Koordinaten:', features[0].geometry.coordinates);
    }

    return features;
  }

  private convertFeatureToPoint(feature: any): AufnahmePunkt | null {
    if (!feature.geometry || feature.geometry.type !== 'Point') {
      return null;
    }

    const coords = feature.geometry.coordinates;
    if (!coords || coords.length < 2) {
      return null;
    }

    const props = feature.properties || {};
    const crs = feature.crs; // EPSG-Code aus GeoPackage

    // Debug: Alle Properties ausgeben
    console.log('GeoPackage Feature Properties:', Object.keys(props), props);

    // SYMBOL zu Punktart konvertieren
    const symbol = parseInt(props.SYMBOL || props.symbol || '6', 10);
    const punktart = punktartFromSymbol(symbol) || '6_Tiefbohrung';

    // Neuen Punkt aus Template erstellen
    const bp = punktTemplate(punktart);

    // Koordinaten transformieren basierend auf tatsächlichem Format
    // GeoJSON-Standard ist immer WGS84 (EPSG:4326), auch wenn das GeoPackage ein anderes CRS hat
    // Die iterateGeoJSONFeatures-Methode konvertiert bereits zu WGS84
    let epsg5650: { x: number; y: number };

    // Heuristik: Wenn X < 180 und Y < 90, dann sind es WGS84 Lon/Lat Koordinaten
    const isWGS84 = Math.abs(coords[0]) <= 180 && Math.abs(coords[1]) <= 90;

    if (isWGS84) {
      // WGS84 (GeoJSON-Standard) - coords sind [lon, lat]
      const latlng = new LatLng(coords[1], coords[0]);
      const transformed = this.transformService.toEPSG5650(latlng);
      epsg5650 = { x: transformed.x, y: transformed.y };
      console.log(`Koordinaten: WGS84 [${coords[0]}, ${coords[1]}] → EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else if (coords[0] > 32000000 && coords[0] < 34000000) {
      // Bereits EPSG:5650 (mit Zone-Präfix 33)
      epsg5650 = { x: coords[0], y: coords[1] };
      console.log(`Koordinaten: Bereits EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else if (coords[0] > 200000 && coords[0] < 800000 && coords[1] > 5000000) {
      // EPSG:25833 (UTM Zone 33N ohne Präfix)
      const transformed = this.transformService.toEPSG5650(
        this.transformService.from({ system: LocationSystem.EPSG25833, x: coords[0], y: coords[1] })
      );
      epsg5650 = { x: transformed.x, y: transformed.y };
      console.log(`Koordinaten: EPSG:25833 [${coords[0]}, ${coords[1]}] → EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else {
      // Fallback: Als WGS84 behandeln
      console.warn(`Unbekanntes Koordinatenformat: [${coords[0]}, ${coords[1]}], versuche als WGS84`);
      const latlng = new LatLng(coords[1], coords[0]);
      const transformed = this.transformService.toEPSG5650(latlng);
      epsg5650 = { x: transformed.x, y: transformed.y };
    }

    bp.header.koordinaten = {
      system: LocationSystem.EPSG5650,
      x: epsg5650.x,
      y: epsg5650.y
    };

    // SYM_TEXT: Bei Vollprotokoll-Punkten als Nummer, sonst als Bemerkungen.
    // Immer als Text: Eine Zahlenspalte liefert Zahlen, und eine Zahl als Nummer
    // liess die Punktliste abbrechen (Befund 29.09.2026).
    const symText = String(props.SYM_TEXT || props.sym_text || '');
    if (istPunktartMitProtokoll(punktart)) {
      // Vollprotokoll: SYM_TEXT → Nummer
      bp.header.nummer = symText || String(props.Nummer || props.nummer || '');
    } else {
      // Kurzprotokoll/andere: SYM_TEXT → Bemerkungen, Nummer separat
      bp.header.nummer = String(props.Nummer || props.nummer || '');
      if (symText) {
        bp.header.bemerkungen = symText;
      }
    }

    // Bodenform: HBF + BF zusammensetzen.
    //   HBF: ggf. mehrere durch `/`/`//` getrennt, mit Substratpraefix-Normalisierung
    //   BF:  Praefix(e) + ggf. mehrere FBFs durch `//` (begrabener Boden) +
    //        optional Wasserstufen + optional /Substrat-Suffix + optional [-_]Substratfeuchte
    const hbfRaw = props.HBF || props.hbf || '';
    const bfRaw = props.BF || props.bf || '';
    const parsedHbfs = parseHbfList(hbfRaw);
    const parsedBf = parseFeinbodenform(bfRaw);

    // bf[]-Eintraege paarweise aus HBF- und FBF-Listen aufbauen — Index-Matching.
    const pairCount = Math.max(parsedHbfs.length, parsedBf.bfPairs.length);
    if (pairCount > 0) {
      const bfArray: BodenformPair[] = [];
      for (let i = 0; i < pairCount; i++) {
        const h = parsedHbfs[i];
        const f = parsedBf.bfPairs[i];
        // Trenner: bevorzugt aus FBF-Pair (das ist der semantisch wichtige `//`-Marker
        // fuer „begrabener Boden"); fallback HBF.
        const trenner = (f?.trenner ?? h?.trenner ?? '') as '' | '/' | '//';
        const pair: BodenformPair = {
          trenner,
          hbf: h?.hbf ?? '',
          fbf: f?.fbf ?? '',
        };
        if (f?.prefix) pair.prefix = f.prefix;
        bfArray.push(pair);
      }
      bp.header.bodenform.bf = bfArray;
    }

    if (parsedBf.suffix) bp.header.bodenform.suffix = parsedBf.suffix;
    if (parsedBf.reliktG) bp.header.bodenform.reliktG = true;
    if (parsedBf.stauwasser) bp.header.wasserstufe2 = parsedBf.stauwasser;
    if (parsedBf.grundwasser) bp.header.wasserstufe = parsedBf.grundwasser;
    if (parsedBf.substratFeuchte) bp.header.substratFeuchte = parsedBf.substratFeuchte;

    // HF (Humusform) übernehmen
    const hf = props.HF || props.hf || '';
    if (hf) {
      if (symbol === 7) {
        // Moorpunkt: HF ist Vegetationsformengruppe
        bp.header.vegetationsformengruppe = hf;
      } else {
        bp.header.humusform.aufnahmeFlaeche.neu = hf;
      }
    }

    // Bemerkungen übernehmen (nur wenn nicht bereits durch SYM_TEXT gesetzt)
    const bemerkungenFromProps = props.Bemerkungen || props.bemerkungen || '';
    if (bemerkungenFromProps && !bp.header.bemerkungen) {
      bp.header.bemerkungen = bemerkungenFromProps;
    }

    return bp;
  }

  private validateParsedData(data: AufnahmePunkt, result: ImportValidationResult): void {
    // Koordinaten prüfen
    if (!data.header.koordinaten?.x || !data.header.koordinaten?.y) {
      result.isValid = false;
      result.messages.push({
        severity: 'error',
        field: 'Koordinaten',
        value: '',
        message: 'Koordinaten fehlen oder sind ungültig'
      });
    }

    // Nummer prüfen (Warnung wenn leer)
    if (!data.header.nummer) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Nummer',
        value: '',
        message: 'Bohrpunktnummer ist leer'
      });
    }

    // Datum prüfen
    if (!data.header.aufnahme?.datum) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Datum',
        value: '',
        message: 'Aufnahmedatum fehlt'
      });
    }

    // Kartierer prüfen
    if (!data.header.aufnahme?.kartierer) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Kartierer',
        value: '',
        message: 'Kartierer fehlt'
      });
    }

    // TODO: Weitere Validierungen (Substrate, Horizonte, Vegetation gegen Listen prüfen)
  }

  private createValidationSummary(): void {
    const results: ImportValidationResult[] = [];
    let successCount = 0;
    let warningCount = 0;
    let errorCount = 0;

    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        results.push(sheet.validation);
        if (!sheet.validation.isValid) {
          errorCount++;
        } else if (sheet.validation.hasWarnings) {
          warningCount++;
        } else {
          successCount++;
        }
      }
    }

    this.validationSummary = {
      totalFiles: results.length,
      successCount,
      warningCount,
      errorCount,
      results
    };
  }

  private async checkDuplicates(): Promise<void> {
    this.duplicates = [];
    this.progressText = 'Prüfe auf Duplikate...';

    // Bestehende Punkte aus DB laden
    try {
      const result = await this.dataService.queryIndex(true);
      if (result?.rows) {
        this.existingPoints = result.rows.map((row: any) => row.doc as AufnahmePunktDocument);
      }
    } catch (error) {
      console.error('Fehler beim Laden bestehender Punkte:', error);
      return;
    }

    // Alle zu importierenden Punkte prüfen
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (!sheet.data || !sheet.validation.isValid) continue;

        const newPoint = sheet.data;
        const duplicate = this.findDuplicate(newPoint);

        if (duplicate) {
          this.duplicates.push(duplicate);

          // Warnung zur Validierung hinzufügen
          sheet.validation.hasWarnings = true;
          sheet.validation.messages.push({
            severity: 'warning',
            field: 'Duplikat',
            value: duplicate.existingNummer || duplicate.existingId,
            message: this.getDuplicateMessage(duplicate)
          });
        }
      }
    }

    // Summary aktualisieren falls Duplikate gefunden
    if (this.duplicates.length > 0) {
      this.createValidationSummary();
    }
  }

  private findDuplicate(newPoint: AufnahmePunkt): DuplicateInfo | null {
    const COORDINATE_TOLERANCE = 5; // Meter

    for (const existing of this.existingPoints) {
      // 1. Koordinaten-Prüfung (< 5m Entfernung) - ERSTES Kriterium
      if (newPoint.header.koordinaten && existing.header?.koordinaten) {
        const distance = this.calculateDistance(
          newPoint.header.koordinaten,
          existing.header.koordinaten
        );

        if (distance < COORDINATE_TOLERANCE) {
          return {
            newPoint,
            existingId: existing._id,
            existingNummer: existing.header?.nummer,
            matchType: 'koordinaten',
            distance,
            foreignProjectName: this.foreignProjectNameOf(existing)
          };
        }
      }

      // 2. Kombination: Nummer + Datum + Kartierer (alle 3 müssen übereinstimmen)
      if (newPoint.header.nummer && existing.header?.nummer &&
          newPoint.header.aufnahme?.datum && existing.header?.aufnahme?.datum &&
          newPoint.header.aufnahme?.kartierer && existing.header?.aufnahme?.kartierer) {

        // Nummer prüfen (case-insensitive, toString() falls Zahl)
        const nummerMatch = String(newPoint.header.nummer).trim().toLowerCase() ===
                            String(existing.header.nummer).trim().toLowerCase();

        // Datum prüfen (nur Datum, nicht Uhrzeit)
        const newDate = new Date(newPoint.header.aufnahme.datum).toDateString();
        const existingDate = new Date(existing.header.aufnahme.datum).toDateString();
        const datumMatch = newDate === existingDate;

        // Kartierer prüfen (case-insensitive, toString() falls nötig)
        const kartiererMatch = String(newPoint.header.aufnahme.kartierer).trim().toLowerCase() ===
                               String(existing.header.aufnahme.kartierer).trim().toLowerCase();

        // Alle 3 Kriterien erfüllt = Duplikat
        if (nummerMatch && datumMatch && kartiererMatch) {
          return {
            newPoint,
            existingId: existing._id,
            existingNummer: existing.header.nummer,
            matchType: 'datum_kartierer',
            foreignProjectName: this.foreignProjectNameOf(existing)
          };
        }
      }
    }

    return null;
  }

  /**
   * Gehoert der gefundene Punkt zu einem ANDEREN Projekt als dem aktiven? Dann
   * dessen Name, sonst `undefined`.
   *
   * Der Duplikat-Abgleich laeuft datenbankweit (anders als im Patch-Modus, der
   * aufs aktive Projekt filtert) — ein Treffer kann also ein fremder Projektpunkt
   * sein. Geprueft werden BEIDE Zuordnungen: die App-Mitgliedschaft
   * (`header.projects`) und der Workflow (`workflow.projectId`); zeigt eine davon
   * aufs aktive Projekt, ist der Punkt nicht fremd. Projektlose Altpunkte sind
   * ebenfalls nicht fremd — die darf der Import wie bisher uebernehmen.
   */
  private foreignProjectNameOf(existing: AufnahmePunktDocument): string | undefined {
    const activeId = this.projectService.project();
    if (!activeId) return undefined;

    const projects = existing.header?.projects ?? [];
    const workflowProjectId: string | undefined = (existing as any).workflow?.projectId;

    if (workflowProjectId === activeId) return undefined;
    if (projects.some(p => p?.id === activeId)) return undefined;

    const fremd = foreignProjectOf(projects, activeId);
    if (fremd) return fremd.name || fremd.id;
    if (workflowProjectId) {
      return this.patchProjects.find(p => p._id === workflowProjectId)?.name ?? workflowProjectId;
    }
    return undefined; // projektloser Altpunkt
  }

  private calculateDistance(coord1: { x: number; y: number }, coord2: { x: number; y: number }): number {
    // Euklidische Distanz für projizierte Koordinaten (EPSG:5650/25833)
    const dx = coord1.x - coord2.x;
    const dy = coord1.y - coord2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getDuplicateMessage(duplicate: DuplicateInfo): string {
    let text: string;
    switch (duplicate.matchType) {
      case 'koordinaten':
        text = `Punkt mit gleichen Koordinaten existiert bereits (${duplicate.distance?.toFixed(1)}m entfernt)`;
        break;
      case 'datum_kartierer':
        text = `Punkt mit gleicher Nummer, Datum und Kartierer existiert bereits`;
        break;
      default:
        text = `Duplikat gefunden`;
    }
    // Cross-Projekt-Treffer sind nie ueberschreibbar — das gehoert schon in die
    // Vorschau, nicht erst in die Meldung nach dem Import.
    if (duplicate.foreignProjectName) {
      text += ` — im Projekt „${duplicate.foreignProjectName}". Dieser Punkt wird übersprungen (fremde Projekte werden nie überschrieben).`;
    }
    return text;
  }

  // === Vorschau ===

  get validPoints(): AufnahmePunkt[] {
    const points: AufnahmePunkt[] = [];
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (sheet.data && sheet.validation.isValid) {
          points.push(sheet.data);
        }
      }
    }
    return points;
  }

  get warningPoints(): AufnahmePunkt[] {
    const points: AufnahmePunkt[] = [];
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (sheet.data && sheet.validation.isValid && sheet.validation.hasWarnings) {
          points.push(sheet.data);
        }
      }
    }
    return points;
  }

  get pointsToImport(): number {
    let count = 0;
    if (this.importOptions.importValid) {
      count += this.validationSummary?.successCount || 0;
    }
    if (this.importOptions.importWithWarnings) {
      count += this.validationSummary?.warningCount || 0;
    }
    return count;
  }

  async copyValidationToClipboard(): Promise<void> {
    if (!this.validationSummary) return;

    const text = formatValidationForClipboard(this.validationSummary);
    const success = await copyToClipboard(text);

    if (success) {
      this.toastr.success('Validierungsbericht in Zwischenablage kopiert');
    } else {
      this.toastr.error('Kopieren fehlgeschlagen');
    }
  }

  // === Import ===

  async startImport(): Promise<void> {
    // Ohne aktives Projekt gibt es keine Projektzuordnung — die Punkte waeren in
    // der App weder bearbeitbar (Feld-Edit-Gate §5.1) noch im Projekt-Backup
    // enthalten. Darum hart blockieren statt stillschweigend Datenmuell anzulegen
    // (User-Entscheidung 2026-07-11). Der Import-Knopf ist zusaetzlich deaktiviert.
    if (!this.projectService.project()) {
      this.toastr.error('Bitte zuerst ein Projekt öffnen — importierte Punkte brauchen eine Projektzuordnung.');
      this.step = 'preview';
      return;
    }

    this.step = 'importing';
    this.progress = 0;
    this.importedCount = 0;
    this.skippedCount = 0;

    const pointsToImport: AufnahmePunkt[] = [];
    const pointsToUpdate: { existing: AufnahmePunktDocument; newData: AufnahmePunkt }[] = [];
    /** Duplikate, die zu einem fremden Projekt gehoeren — nur gemeldet, nie geschrieben. */
    const foreignSkipped: DuplicateInfo[] = [];

    // Duplikat-IDs für schnellen Lookup
    const duplicatePointMap = new Map<AufnahmePunkt, DuplicateInfo>();
    for (const dup of this.duplicates) {
      duplicatePointMap.set(dup.newPoint, dup);
    }

    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (!sheet.data) continue;

        // Import-Info zum Punkt hinzufügen
        if (sheet.importInfo) {
          sheet.data.importInfo = sheet.importInfo;
        }

        // Prüfen ob dieser Punkt ein Duplikat ist
        const duplicate = duplicatePointMap.get(sheet.data);
        const hasDuplicate = !!duplicate;

        // Prüfen ob Punkt aufgrund Validierung importiert werden soll
        const passesValidation =
          (sheet.validation.isValid && !sheet.validation.hasWarnings && this.importOptions.importValid) ||
          (sheet.validation.isValid && sheet.validation.hasWarnings && this.importOptions.importWithWarnings);

        if (!passesValidation) {
          this.skippedCount++;
          continue;
        }

        // Cross-Projekt-Schutz (User-Entscheidung 2026-07-11): Der Duplikat-
        // Abgleich laeuft datenbankweit. Trifft er einen Punkt eines FREMDEN
        // Projekts, wird nie ueberschrieben — sonst zerschreibt der Import fremde
        // Projektdaten und zoege den Punkt via header.projects-Stempel ins eigene
        // Projekt. Solche Treffer werden uebersprungen und unten gemeldet.
        if (hasDuplicate && duplicate.foreignProjectName) {
          foreignSkipped.push(duplicate);
          this.skippedCount++;
          continue;
        }

        // Duplikat-Behandlung (User-Wunsch 2026-06-05): nur noch `skip` und
        // `update`. `keep_both` entfaellt. `update` ist permission-gated und
        // wird fuer Nicht-Manager/Entwickler defensiv auf `skip` heruntergesetzt
        // (UI deaktiviert die Option zusaetzlich — siehe canIOverwriteOnImport).
        if (hasDuplicate) {
          switch (this.effectiveDuplicateAction) {
            case 'skip':
              this.skippedCount++;
              continue;

            case 'update':
              // Bestehenden Punkt zum Update vormerken
              const existing = this.existingPoints.find(p => p._id === duplicate.existingId);
              if (existing) {
                pointsToUpdate.push({ existing, newData: sheet.data });
              }
              continue;
          }
        } else {
          // Kein Duplikat - normal importieren
          pointsToImport.push(sheet.data);
        }
      }
    }

    // Cross-Projekt-Treffer melden — auch dann, wenn sonst nichts zu tun ist
    // (sonst schliesst der Wizard kommentarlos und der User haelt die Punkte
    // faelschlich fuer importiert).
    this.reportForeignSkipped(foreignSkipped);

    const totalOperations = pointsToImport.length + pointsToUpdate.length;
    if (totalOperations === 0) {
      this.step = 'complete';
      return;
    }

    // Workflow-Default 2026-06-05: importierte Punkte landen IMMER als
    // `bearbeitung` (User-Wunsch — sonst erschienen sie als „Bestand" und
    // waren nicht ohne Umweg bearbeitbar). Greift fuer Neu- UND Update-Pfad;
    // restartWorkflow erhaelt dabei History als Audit-Trail.
    this.forceBearbeitungWorkflow(pointsToImport);
    this.forceBearbeitungWorkflow(pointsToUpdate.map(p => p.newData));

    // Projekt-Mitgliedschaft (`header.projects`) symmetrisch zum Workflow setzen —
    // ohne sie sind die Punkte im geoeffneten Projekt read-only (User-Befund
    // 2026-07-11, siehe assignActiveProject).
    this.assignActiveProject(pointsToImport);
    this.assignActiveProject(pointsToUpdate.map(p => p.newData));

    // Bestand: sicher Erkanntes direkt in SEA-Form konvertieren; Unsicheres bleibt
    // Rohtext (der „Bestand"-Validator markiert es gelb). Original immer im Freitext.
    for (const p of pointsToImport) convertOnImport(p.extendedHeader);
    for (const p of pointsToUpdate) convertOnImport(p.newData.extendedHeader);

    // Import-Status-Klemme (H-Regel-1/2, C4-Fix 2026-06-04 / M-7.6-Import-
    // Status-Klemme) — zweite Verteidigungslinie. Seit der Force-Bearbeitung
    // oben effektiv ein No-Op, bleibt aber als Symmetrie zum Reimport-Pfad
    // in `alle-bohrpunkte.component` und als Schutz gegen kuenftige
    // Aenderungen an forceBearbeitungWorkflow erhalten.
    const clampNew = this.clampImportedWorkflows(pointsToImport);
    const clampUpdates = this.clampImportedWorkflows(pointsToUpdate.map(p => p.newData));
    const totalDowngraded = clampNew.downgraded + clampUpdates.downgraded;

    // Neue Punkte importieren
    if (pointsToImport.length > 0) {
      this.progressText = `Importiere ${pointsToImport.length} neue Punkte...`;

      try {
        await this.dataService.addBohrpunkte(pointsToImport);
        this.importedCount += pointsToImport.length;
      } catch (error: any) {
        this.toastr.error(`Import fehlgeschlagen: ${error.message}`);
      }
    }

    // Bestehende Punkte aktualisieren
    if (pointsToUpdate.length > 0) {
      this.progressText = `Aktualisiere ${pointsToUpdate.length} bestehende Punkte...`;
      for (const { existing, newData } of pointsToUpdate) {
        try {
          // Merge: Bestehende Dokument-Metadaten beibehalten, Daten aktualisieren
          // _rev wird von PouchDB hinzugefügt, ist aber nicht im TypeScript-Interface
          const existingWithRev = existing as any;
          const updatedDoc = {
            ...existing,
            ...newData,
            _id: existing._id,
            _rev: existingWithRev._rev
          };
          await this.dataService.saveDocument(updatedDoc);
          this.importedCount++;
        } catch (error: any) {
          console.error(`Fehler beim Aktualisieren von ${existing._id}:`, error);
          this.skippedCount++;
        }
      }
    }

    if (this.importedCount > 0) {
      const downgradeNote = totalDowngraded > 0
        ? ` (${totalDowngraded} ohne Freigabe-Rechte auf „Bearbeitung" geklemmt)`
        : '';
      this.toastr.success(`${this.importedCount} Punkte importiert/aktualisiert${downgradeNote}`);
    }

    this.step = 'complete';
  }

  /**
   * Permission-Gate fuer den `update`-Pfad (Duplikat ueberschreiben):
   * NUR Manager/Entwickler — User-Wunsch 2026-06-05. Andere Rollen koennen
   * versehentlich Daten zerstoeren; Default-Verhalten ist daher `skip`.
   * Spiegelt das `canI('manage', 'projects')`-Pattern aus PERMISSIONS.md §4 P3.
   */
  get canIOverwriteOnImport(): boolean {
    return this.authService.canI('manage', 'projects');
  }

  /**
   * Defense-in-Depth: selbst wenn jemand per DOM/URL-Trick
   * `duplicateAction='update'` setzt, ohne berechtigt zu sein, wird die
   * Aktion auf `skip` heruntergesetzt. Die UI deaktiviert die Option
   * zusaetzlich (Two-tier-Schutz).
   */
  get effectiveDuplicateAction(): 'skip' | 'update' {
    const wanted = this.importOptions.duplicateAction;
    if (wanted === 'update' && !this.canIOverwriteOnImport) return 'skip';
    return wanted;
  }

  /**
   * User-Wunsch 2026-06-05: importierte Punkte landen IMMER als
   * `bearbeitung` in der DB — egal ob die Excel-Quelle einen Workflow
   * mitliefert oder nicht, und auch beim Update-Pfad. So sind sie
   * sofort bearbeitbar und tauchen nicht als „Bestand" (workflow=undef)
   * in der Liste auf.
   *
   * - Punkt OHNE Workflow: frischer `initialWorkflow`-Block.
   * - Punkt MIT Workflow: `restartWorkflow` — History bleibt als Audit-
   *   Trail erhalten, ein reset-Entry markiert den Wechsel. Faellt auf
   *   `initialWorkflow` zurueck, wenn kein aktives Projekt verfuegbar
   *   ist (defensiv — sollte praktisch nicht vorkommen).
   *
   * Mutiert die Punkte in-place.
   */
  private forceBearbeitungWorkflow(points: AufnahmePunkt[]): void {
    const user = this.authService.currentUser ?? 'unknown';
    const userId = this.authService.currentUserId ?? '';
    const projectId = this.projectService.project() || undefined;
    const time = Date.now();
    for (const p of points) {
      const anyP = p as any;
      if (anyP.workflow && projectId) {
        // Importierte Punkte landen auf `bearbeitung` (E5) — NICHT im Pool
        // (`anstehend`). restartWorkflow-Default ist seit 2026-06-16 `anstehend`,
        // daher explizit `toStatus: 'bearbeitung'` (E7-Fix der zwei Lib-Commits).
        restartWorkflow(anyP, { toProjectId: projectId, user, userId, time, toStatus: 'bearbeitung' });
      } else {
        anyP.workflow = initialWorkflow({ user, userId, time, projectId });
      }
    }
  }

  /**
   * Meldet uebersprungene Duplikate aus fremden Projekten. Nennt die betroffenen
   * Projekte, damit der User entscheiden kann (z. B. dort importieren oder den
   * Punkt regulaer uebernehmen) — stillschweigendes Verschlucken waere schlimmer
   * als das Ueberschreiben, weil niemand merkt, dass Daten fehlen.
   */
  private reportForeignSkipped(foreignSkipped: DuplicateInfo[]): void {
    if (foreignSkipped.length === 0) return;
    const projekte = [...new Set(foreignSkipped.map(d => d.foreignProjectName!))];
    const liste = projekte.join(', ');
    this.toastr.warning(
      `${foreignSkipped.length} Punkt(e) übersprungen: Es gibt sie bereits in einem anderen Projekt (${liste}). ` +
      `Punkte aus fremden Projekten werden beim Import nie überschrieben.`
    );
  }

  /**
   * Ordnet die importierten Punkte dem AKTIVEN Projekt zu (`header.projects`).
   *
   * Notwendig, weil `header.projects` — nicht `workflow.projectId` — das
   * Feld-Edit-Gate der App speist (`canIWritePoint(bp) &&
   * isPointAssignedToActiveProject(bp)`, PERMISSIONS.md §5.1) und ausserdem der
   * Filter ist, ueber den das Projekt-Backup seine Punkte einsammelt. Ohne
   * Eintrag landeten importierte Punkte als sichtbare, aber read-only Punkte im
   * Projekt („nicht zugeordnet"-Warnung) — User-Befund 2026-07-11.
   *
   * Einfachmodell: genau EIN Projekt je Punkt — der Stempel ersetzt eine
   * eventuelle Alt-Zuordnung, analog zur Uebernahme (`point-takeover.ts`) und
   * passend zum `restartWorkflow` ins aktive Projekt in
   * `forceBearbeitungWorkflow`. Mutiert die Punkte in-place.
   */
  private assignActiveProject(points: AufnahmePunkt[]): void {
    const projectId = this.projectService.project();
    if (!projectId) return; // startImport blockiert diesen Fall bereits
    const name = this.projectService.currentProjectDoc()?.name
      ?? this.patchProjects.find(p => p._id === projectId)?.name
      ?? projectId;
    for (const p of points) {
      p.header.projects = [{ id: projectId, name }];
    }
  }

  // =====================================================================
  // Restore-Modus (User-Wunsch 2026-06-06 / Projekt-Backup)
  // =====================================================================

  /**
   * Initialisiert den Wizard im Restore-Modus mit bereits-migrierten
   * Backup-Daten. Springt direkt in den Preview-Step — kein File-Picker,
   * keine Layer-Wahl, keine Excel-Analyse.
   *
   * Voraussetzungen (Caller-Verantwortung):
   *  - `project` ist das Projekt-Doc aus dem Backup (ohne `_rev`)
   *  - `points` sind die Punkt-Dokumente, bereits durch
   *    `applyBackupMigrations` auf CURRENT_DATA_VERSION migriert
   *  - `warnings` sind die gesammelten dataLossWarnings (werden im
   *    Preview prominent angezeigt)
   */
  async startRestore(
    project: ProjectDocument,
    points: AufnahmePunktDocument[],
    warnings: string[] = [],
    attachments: Record<string, Uint8Array> = {}
  ): Promise<void> {
    this.mode = 'restore';
    this.restoreProject = project;
    this.restoreMigrationWarnings = warnings;
    this.restoreAttachments = attachments;
    this.step = 'analyzing';
    this.progressText = 'Lade bestehende Punkte des Projekts...';

    // Bestehende Punkte aus der DB laden — Permission-Check pro Punkt
    // braucht den DB-Stand (Status × Ownership), nicht den Backup-Stand.
    try {
      const result = await this.dataService.queryIndex(true);
      this.existingPoints = result?.rows
        ? result.rows.map((row: any) => row.doc as AufnahmePunktDocument)
        : [];
    } catch (error) {
      console.error('Fehler beim Laden bestehender Punkte:', error);
      this.toastr.error('Bestehende Punkte konnten nicht geladen werden');
      this.step = 'select';
      return;
    }

    this.evaluateRestoreItems(points);
    this.step = 'preview';
  }

  /**
   * Befuellt `restoreItems` aus den Backup-Punkten. Pro Punkt:
   *  - existiert er in der DB (nach `_id`)? → overwritable / blocked
   *  - existiert er nicht? → new
   *
   * Permission-Check fuer Overwrite ist `canIRestoreOverwritePoint(existing)`
   * gegen den DB-Stand (Status × Rolle × Ownership × LOCK × Frozen). Neue
   * Punkte werden immer angelegt (Permission haengt nicht am Status — der
   * Punkt existiert ja noch nicht).
   */
  private evaluateRestoreItems(backupPoints: AufnahmePunktDocument[]): void {
    const items: RestoreItem[] = [];
    for (const bp of backupPoints) {
      const existing = this.existingPoints.find(p => p._id === bp._id) ?? null;
      if (!existing) {
        items.push({ point: bp, existing: null, status: 'new', selected: true });
        continue;
      }
      const allowed = this.projectService.canIRestoreOverwritePoint(existing);
      if (allowed) {
        items.push({ point: bp, existing, status: 'overwritable', selected: true });
      } else {
        items.push({
          point: bp,
          existing,
          status: 'blocked',
          selected: false,
          reason: this.explainOverwriteBlock(existing)
        });
      }
    }
    this.restoreItems = items;
  }

  /** Klartext-Begruendung, warum ein Punkt nicht ueberschrieben werden darf. */
  private explainOverwriteBlock(existing: AufnahmePunktDocument): string {
    const status = (existing as any)?.workflow?.status ?? 'Bestand';
    if (this.projectService.isCurrentProjectFinalized()) {
      return 'Projekt ist endgueltig abgeschlossen (LOCK) — kein Restore moeglich.';
    }
    if (this.projectService.isPointFrozen(existing as any)) {
      return `Punkt eingefroren (Status ${status}, fremdes Projekt).`;
    }
    return `Status „${status}" — Ihre Rolle darf diesen Punkt nicht ueberschreiben.`;
  }

  /** Setzt die Default-Haken nach Wechsel der globalen Strategie. */
  applyRestoreStrategy(): void {
    for (const item of this.restoreItems) {
      if (item.status === 'blocked') {
        item.selected = false;
        continue;
      }
      if (this.restoreStrategy === 'only-new') {
        item.selected = item.status === 'new';
      } else {
        item.selected = item.status === 'new' || item.status === 'overwritable';
      }
    }
  }

  // Counts fuer die Preview-Summary
  get restoreNewCount(): number { return this.restoreItems.filter(i => i.status === 'new').length; }
  get restoreOverwritableCount(): number { return this.restoreItems.filter(i => i.status === 'overwritable').length; }
  get restoreBlockedCount(): number { return this.restoreItems.filter(i => i.status === 'blocked').length; }
  get restoreSelectedCount(): number { return this.restoreItems.filter(i => i.selected).length; }

  /**
   * Fuehrt den Restore aus: ProjektDoc-Merge + ausgewaehlte Punkte schreiben +
   * aktives Projekt setzen. Restore-Pfad ueberspringt `forceBearbeitungWorkflow`
   * (Backup-Workflows bleiben 1:1), nutzt aber `clampImportedWorkflows` als
   * zweite Verteidigungslinie.
   */
  async applyRestore(): Promise<void> {
    if (this.mode !== 'restore' || !this.restoreProject) return;

    const projectId = this.restoreProject._id;
    const inList = this.projectService.projectList?.()?.find(p => p._id === projectId) ?? null;

    // Existiert das Projekt? Dann das VOLLE Doc laden (der projectList-Eintrag ist
    // getrimmt). Es dient doppelt:
    //  (a) Merge-Basis: `mergeRestoredProjectDoc` baut auf `{...dbProject}` auf —
    //      mit dem getrimmten Eintrag wuerde `upsert` Doc-Felder wie
    //      `zeichnungslayer`/`mapDefinition` wegschreiben, die in keiner
    //      restaurierten Kategorie liegen (Datenverlust, z.B. beim Kontrolleur).
    //  (b) Gate-Kontext: `canEditDrawings/Regions` + `isCurrentProjectFinalized`
    //      lesen `currentProjectDoc` und brauchen bearbeiter/kontrolleure/workflow.
    //      Der getrimmte Listen-Eintrag (E2E: by_member-View) traegt das nicht
    //      verlaesslich — dann saehe selbst ein Owner/Bearbeiter alle Kategorien
    //      faelschlich „uebersprungen" (Bug, von E2E M1 aufgedeckt).
    let dbProject: any = inList;
    if (inList) {
      try {
        const full = await this.projectService.getOne(projectId);
        if (full) dbProject = full;
      } catch (e) {
        console.warn('Restore: volles Ziel-Doc nicht ladbar — nutze Listen-Eintrag.', e);
      }
      this.projectService.currentProjectDoc.set(dbProject);
      if (this.projectService.isCurrentProjectFinalized()) {
        this.toastr.error('Projekt ist endgueltig abgeschlossen — Restore nicht moeglich.');
        return;
      }
    }
    // Neues Projekt: existiert noch nicht → kein LOCK moeglich; der User wird
    // beim Anlegen Owner und hat damit volle Rechte (Gates unten = true).

    this.step = 'importing';
    this.progress = 0;
    this.progressText = 'Stelle Projekt-Daten wieder her...';

    // 1) Projekt-Doc mergen (oder neu anlegen) — permission-gegated + strategie-
    // abhaengig. Bereiche→canEditRegions, Zeichnungen+Layer→canEditDrawings.
    // Neues Projekt → User wird Owner (alle Kategorien). DB behaelt Mitglieder/
    // Owner/workflow/archived/pendingInvites (Restore eskaliert keine Rechte).
    const gates: RestoreDataGates = dbProject
      ? {
          canDrawingsAndLayers: this.projectService.canEditDrawings?.() ?? true,
          canRegions: this.projectService.canEditRegions?.() ?? true
        }
      : { canDrawingsAndLayers: true, canRegions: true };
    const merged = mergeRestoredProjectDoc(this.restoreProject, dbProject, {
      currentUserId: this.authService.currentUserId ?? '',
      currentUser: this.authService.currentUser ?? '',
      gates,
      strategy: this.projectDataStrategy
    });
    const projectToSave = merged.doc;
    this.restoreDataRestored = merged.restored;
    this.restoreDataSkipped = merged.skipped;

    // S2: Restaurierte Zeichnungs-Features in die dedizierte DB schreiben — der
    // Loader liest Features aus dem Store, nicht aus dem Doc-Array. VOR dem upsert,
    // damit bei Store-Fehler das Doc-Array (Write-Through) + die Einmal-Migration
    // als Fallback greifen. Gegated über das Zeichnungen-Recht.
    let zeichnungenEingesaet = false;
    if (gates.canDrawingsAndLayers) {
      try {
        const seeded = await seedRestoredDrawings(
          this.zeichnungen, projectId, projectToSave.zeichnungslayer, !dbProject, this.projectDataStrategy,
        );
        zeichnungenEingesaet = seeded;
        if (seeded) projectToSave.zeichnungenMigrated = true; // Loader liest Features aus dem Store
      } catch (e) {
        console.warn('[Restore] Zeichnungen-Store-Seed fehlgeschlagen — Fallback Doc-Array/Migration:', e);
        this.ces.melde('[Restore] Zeichnungen nur aus dem Doc-Array', e);
      }
    }

    // P5 (PLAN-bereiche-db.md): Bereiche in die dedizierte Bereiche-DB seeden,
    // wenn das ZIEL-Projekt schon migriert ist — die Store-Welt ignoriert die
    // Doc-Keys. Neue/nicht-migrierte Ziele: P4-Auto-Migration beim ersten Oeffnen.
    if (gates.canRegions) {
      try {
        await seedRestoredBereiche(
          this.bereiche, projectId, projectToSave,
          this.projectDataStrategy, !!dbProject?.bereicheMigrated
        );
      } catch (e) {
        console.warn('[Restore] Bereiche-Store-Seed fehlgeschlagen — Doc-Keys bleiben Quelle:', e);
        this.ces.melde('[Restore] Bereiche nur aus den Doc-Keys', e);
      }
    }

    try {
      // upsert(): legt das Doc an, wenn noch nicht vorhanden; sonst Update
      // mit _rev-Sync. `saveDocument` (DataService = Punkte-DB) waere doppelt
      // falsch: andere DB UND 404-Frueh-Return fuer neue Projekte.
      await (this.projectService as any).upsert(projectToSave);
    } catch (e: any) {
      this.toastr.error('Projekt-Doc konnte nicht gespeichert werden: ' + (e?.message ?? 'unbekannt'));
      this.step = 'preview';
      return;
    }

    // 2) Aktives Projekt sicherstellen (neues Projekt ist jetzt ladbar).
    if (!dbProject) {
      try { await this.projectService.loadProject(projectId); } catch (e) {
        console.warn('loadProject (neues Projekt) fehlgeschlagen.', e);
      }
      // 2.1) Die Zeichnungen eines NEUEN Projekts wurden oben vor dem Projekt
      // eingesät: ohne Gruppen (das Projekt war noch nicht das offene) und mit
      // einem Upload ins Leere (es stand noch nicht auf dem Server). Jetzt, wo es
      // steht, stempeln und hochladen (luzula-api PLAN-projektstempel.md V2).
      if (zeichnungenEingesaet) await this.zeichnungenNachDemProjekt(projectId, projectToSave.groups);
    }

    // 2.5) Binaere Geodaten-Attachments re-attachen — nur mit Layer-Recht und
    // nur, was im finalen Doc gelistet ist (keine verwaisten Anhaenge).
    await this.reattachRestoredAttachments(projectId, projectToSave, gates.canDrawingsAndLayers);

    // 3) Selektierte Punkte schreiben. Backup-Workflow bleibt 1:1 erhalten;
    // clampImportedWorkflows als Defense-in-Depth. NEUE Punkte (existing=null)
    // gehen via addBohrpunkte/bulkDocs — `saveDocument` faellt sonst auf
    // `database.get(_id)` rein (404 fuer neue Docs → kein Save). UPDATES
    // gehen via saveDocument mit existing._rev.
    const selected = this.restoreItems.filter(i => i.selected);
    const pointsToInsert: AufnahmePunktDocument[] = [];
    const pointsToUpdate: AufnahmePunktDocument[] = [];
    for (const item of selected) {
      const existing = item.existing;
      if (existing) {
        const merged: any = {
          ...existing,
          ...item.point,
          _id: existing._id,
          _rev: (existing as any)._rev
        };
        // E6 (Bestand abgeschafft): Originalstatus aus dem Backup bleibt 1:1
        // erhalten; nur ein fehlender/`null`-Status wird zu `abgeschlossen`.
        materializeLegacyBestand(merged);
        pointsToUpdate.push(merged);
      } else {
        const fresh: any = { ...item.point };
        delete fresh._rev;
        materializeLegacyBestand(fresh); // E6: null→abgeschlossen, sonst unverändert
        pointsToInsert.push(fresh);
      }
    }
    // KEIN clampImportedWorkflows im Restore-Pfad: Spec sagt Backup-Workflow
    // bleibt 1:1 (siehe PERMISSIONS.md §7 Restore-Tabelle + L2-Test). clamp
    // wuerde sonst auch fuer `bearbeitung`-Status den workflow-Block ersetzen
    // (neuer time, leere history) — das zerschiesst die Daten-Integritaet
    // gemessen via Re-Backup-Diff (L1, L4). Die Permission-Sicherung greift
    // bereits in `evaluateRestoreItems` via canIRestoreOverwritePoint.
    // (Einzige Ausnahme oben: `materializeLegacyBestand` hebt einen FEHLENDEN
    // Status auf `abgeschlossen` — „Bestand" ist abgeschafft, E6. Punkte mit
    // echtem Status bleiben unangetastet.)
    this.restoredCount = 0;
    // IP-SPERRE-SCHUTZ (User-Befund 2026-06-16): Online spricht die App direkt
    // mit der Remote-CouchDB (checkout-checkin → db() = Remote). Ein pro-Punkt
    // `put()` ist deshalb EIN HTTP-Request je Punkt — eine Schleife ueber
    // hunderte Punkte loest eine IP-Sperre aus. Darum gebuendelt: je EIN
    // `bulkDocs` fuer Inserts und Updates (analog zum Import via addBohrpunkte).
    // Inserts (kein _rev) → PouchDB legt sie an; Updates (mit _rev) → ueberschreibt.
    // `bulkDocs` invalidiert den queryIndex-Cache NICHT selbst (anders als der
    // put-Override) — das holen wir unten einmal nach.
    if (pointsToInsert.length > 0) {
      try {
        await this.dataService.bulkDocs(pointsToInsert as any);
        this.restoredCount += pointsToInsert.length;
      } catch (e: any) {
        console.error('Restore-Insert (bulkDocs) fehlgeschlagen', e);
        this.skippedCount += pointsToInsert.length;
      }
    }
    if (pointsToUpdate.length > 0) {
      try {
        await this.dataService.bulkDocs(pointsToUpdate as any);
        this.restoredCount += pointsToUpdate.length;
      } catch (e: any) {
        console.error('Restore-Update (bulkDocs) fehlgeschlagen', e);
        this.skippedCount += pointsToUpdate.length;
      }
    }
    // Query-Index-Cache leeren, damit der folgende Reload den frischen Stand sieht.
    this.dataService.invalidateQueryCache();

    // WICHTIG: `upsert` schreibt das restaurierte Projekt-Doc nur in die
    // Projekt-PouchDB. Bereiche/Zeichnungen/Layer werden zur Laufzeit aber aus
    // dem IndexedDB-Keyvalue gelesen (getProjectValue/loadZeichnungslayer) — und
    // der wurde hier noch nicht aktualisiert (loadProject lief fuer den Gate-
    // Kontext VOR dem upsert). Ohne diesen Schritt „kommen Zeichnungen nicht
    // zurueck". Ein erzwungener unload→load synct das frische Doc nach IndexedDB
    // (setProjectPreservingDrawings) und triggert den Karten-Projektwechsel-Effekt
    // (loadZeichnungslayer + Punkte/Layer-Reload).
    try {
      await this.projectService.unloadCurrentProject();
      await this.projectService.loadProject(projectId);
    } catch (e) {
      console.warn('Restore: Projekt-Reload nach dem Schreiben fehlgeschlagen.', e);
    }

    this.toastr.success(
      `Projekt wiederhergestellt: ${this.restoredCount} Punkte geschrieben`
      + (this.skippedCount > 0 ? `, ${this.skippedCount} fehlgeschlagen` : '')
      + (this.restoreDataRestored.length ? ` • ${this.restoreDataRestored.join('/')}` : '')
    );
    if (this.restoreDataSkipped.length) {
      this.toastr.warning(
        `Ohne Berechtigung übersprungen: ${this.restoreDataSkipped.join(', ')}`,
        'Projekt-Daten teilweise nicht wiederhergestellt'
      );
    }
    this.step = 'complete';
  }

  /**
   * Stempelt die beim Restore eines NEUEN Projekts eingesäten Zeichnungen mit
   * den Gruppen des Projekts und lädt sie hoch (`stampeGroups` lädt selbst
   * hoch); ohne Gruppen wird nur hochgeladen. Scheitert es, bleibt der Restore
   * gültig — die Zeichnungen liegen auf dem Gerät und gehen mit dem nächsten
   * Abgleich hoch.
   */
  private async zeichnungenNachDemProjekt(projectId: string, gruppen: unknown): Promise<void> {
    const g = Array.isArray(gruppen) ? gruppen.filter((x): x is string => typeof x === 'string' && !!x) : [];
    try {
      if (g.length) await this.zeichnungen.stampeGroups(projectId, g);
      else await this.zeichnungen.nurHochladen(projectId);
    } catch (e) {
      console.warn('[Restore] Zeichnungen nach dem Projekt nicht hochgeladen:', e);
      this.ces.melde('[Restore] Zeichnungen nach dem Projekt nicht hochgeladen', e);
    }
  }

  /**
   * Re-attached die Geodaten-Blobs aus dem Backup als Projekt-Attachments.
   * Gegated ueber das Layer-Recht; schreibt nur Dateien, die im finalen Doc
   * (`geodataFiles`/`attachmentsMeta`) gelistet sind — sonst entstuenden
   * verwaiste Anhaenge (z.B. beim Merge mit DB-seitiger Datei-Liste).
   * Sequenziell (wenige Dateien) — kein Request-Burst (IP-Sperre-Regel).
   */
  private async reattachRestoredAttachments(projectId: string, savedDoc: any, allowed: boolean): Promise<void> {
    if (!allowed) return;
    const names = Object.keys(this.restoreAttachments ?? {});
    if (names.length === 0) return;
    const listed = new Set<string>([
      ...((savedDoc.geodataFiles ?? []) as string[]),
      ...(((savedDoc.attachmentsMeta ?? []) as any[]).map(m => m?.name).filter(Boolean))
    ]);
    for (const name of names) {
      if (!listed.has(name)) continue;
      try {
        const contentType = this.guessAttachmentContentType(name);
        // Cast: Uint8Array<ArrayBufferLike> → BlobPart (TS-5.7-lib.dom verlangt <ArrayBuffer>).
        const blob = new Blob([this.restoreAttachments[name] as BlobPart], { type: contentType });
        await this.projectService.addProjectAttachment(projectId, name, blob, contentType);
      } catch (e) {
        // Ein fehlender Anhang faellt erst auf, wenn jemand die Karte braucht.
        console.warn('Restore: Geodaten-Attachment fehlgeschlagen:', name, e);
        this.ces.melde('[Restore] Geodaten-Anhang fehlt', e);
      }
    }
  }

  /** MIME-Type nach Datei-Endung (fuers Re-Attachen der Geodaten). */
  private guessAttachmentContentType(name: string): string {
    const lower = name.toLowerCase();
    if (lower.endsWith('.gpkg')) return 'application/geopackage+sqlite3';
    if (lower.endsWith('.geojson') || lower.endsWith('.json')) return 'application/geo+json';
    if (lower.endsWith('.meta')) return 'application/json';
    return 'application/octet-stream';
  }

  /**
   * Klemmt Workflow-Status importierter Punkte gegen `clampImportedWorkflow`
   * (H-Regel-1/2). Symmetrie zum Reimport-Pfad in `alle-bohrpunkte.component`.
   * Wer kein D1-Freigaberecht hat, darf keinen hoeheren Status einschleusen
   * — `abgeschlossen`/`kontrolle`/`in_pruefung`/`abgelehnt` werden auf
   * `bearbeitung` zurueckgesetzt. Manager/Entwickler importieren 1:1.
   *
   * Hinweis 2026-06-05/16: `forceBearbeitungWorkflow` setzt Punkte OHNE
   * Workflow auf `bearbeitung` (initialWorkflow), Punkte MIT Workflow ueber
   * `restartWorkflow` auf `anstehend` (Pool — muss erst „gegriffen" werden).
   * In beiden Faellen ist kein hoher Status mehr drin; clamp bleibt nur noch
   * zweite Verteidigungslinie (Defense-in-Depth), aus Konsistenzgruenden erhalten.
   *
   * Mutiert die Punkte in-place und gibt die Anzahl der heruntergesetzten
   * Workflows zurueck (fuer die Toast-Meldung).
   */
  private clampImportedWorkflows(points: AufnahmePunkt[]): { downgraded: number } {
    const canFreigeben = this.projectService.canIfreigeben();
    const user = this.authService.currentUser;
    const userId = this.authService.currentUserId ?? '';
    let downgraded = 0;
    for (const p of points) {
      const anyP = p as any;
      if (!anyP?.workflow) continue;
      const clamp = clampImportedWorkflow(anyP.workflow, {
        canFreigeben, user, userId, source: 'import-wizard'
      });
      if (clamp.workflow) anyP.workflow = clamp.workflow;
      else delete anyP.workflow;
      if (clamp.downgraded) downgraded++;
    }
    return { downgraded };
  }

  // === Navigation ===

  close(): void {
    this.activeModal.close({
      imported: this.importedCount,
      skipped: this.skippedCount,
      patched: this.patchedCount,
      patchSkipped: this.patchSkippedCount
    });
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  /**
   * Erstellt und speichert eine Import-Logdatei.
   */
  saveImportLog(): void {
    const lines: string[] = [];
    const now = new Date();

    // Header
    lines.push('='.repeat(60));
    lines.push('IMPORT-PROTOKOLL');
    lines.push('='.repeat(60));
    lines.push(`Datum: ${now.toLocaleDateString('de-DE')} ${now.toLocaleTimeString('de-DE')}`);
    lines.push('');

    // Zusammenfassung
    lines.push('-'.repeat(60));
    lines.push('ZUSAMMENFASSUNG');
    lines.push('-'.repeat(60));
    lines.push(`Dateien analysiert: ${this.parsedFiles.length}`);
    if (this.validationSummary) {
      lines.push(`Protokolle gesamt: ${this.validationSummary.totalFiles}`);
      lines.push(`  - Fehlerfrei: ${this.validationSummary.successCount}`);
      lines.push(`  - Mit Warnungen: ${this.validationSummary.warningCount}`);
      lines.push(`  - Mit Fehlern: ${this.validationSummary.errorCount}`);
    }
    lines.push(`Duplikate gefunden: ${this.duplicates.length}`);
    lines.push('');
    lines.push(`Importiert: ${this.importedCount}`);
    lines.push(`Übersprungen: ${this.skippedCount}`);
    lines.push('');

    // Detaillierte Ergebnisse pro Datei
    lines.push('-'.repeat(60));
    lines.push('DETAILS PRO DATEI');
    lines.push('-'.repeat(60));

    for (const file of this.parsedFiles) {
      lines.push('');
      lines.push(`Datei: ${file.filename}`);
      lines.push(`  Sheets: ${file.sheets.length}`);

      for (const sheet of file.sheets) {
        const status = !sheet.validation.isValid ? '[FEHLER]' :
                       sheet.validation.hasWarnings ? '[WARNUNG]' : '[OK]';
        lines.push(`  ${status} ${sheet.name} (${sheet.format})`);

        // Nummer und Koordinaten wenn vorhanden
        if (sheet.data) {
          const nummer = sheet.data.header?.nummer || '(keine Nummer)';
          const koord = sheet.data.header?.koordinaten;
          if (koord) {
            lines.push(`         Nummer: ${nummer}`);
            lines.push(`         Koordinaten: ${koord.x}, ${koord.y}`);
          }
        }

        // Meldungen
        for (const msg of sheet.validation.messages) {
          const prefix = msg.severity === 'error' ? '    [!]' :
                        msg.severity === 'warning' ? '    [?]' : '    [i]';
          lines.push(`${prefix} ${msg.field}: ${msg.message}`);
          if (msg.value) {
            lines.push(`        Wert: "${msg.value}"`);
          }
        }
      }
    }

    // Duplikate
    if (this.duplicates.length > 0) {
      lines.push('');
      lines.push('-'.repeat(60));
      lines.push('DUPLIKATE');
      lines.push('-'.repeat(60));
      for (const dup of this.duplicates) {
        const nummer = dup.newPoint.header?.nummer || '(keine Nummer)';
        lines.push(`  ${nummer} -> existiert bereits als ${dup.existingNummer || dup.existingId}`);
        lines.push(`    Grund: ${dup.matchType === 'koordinaten' ? `Koordinaten (${dup.distance?.toFixed(1)}m)` : 'Nummer/Datum/Kartierer'}`);
      }
    }

    // Patch-Modus: Vegetations-Nachträge
    if (this.mode === 'patch-vegetation' && this.patchMatches.length > 0) {
      lines.push('');
      lines.push('-'.repeat(60));
      lines.push('VEGETATIONS-NACHTRAG (rechte Spalte)');
      lines.push('-'.repeat(60));
      lines.push(`Match-Bereich: ${this.patchProjectLabel}`);
      lines.push(`Sheets analysiert: ${this.patchMatches.length}`);
      lines.push(`  - Bereit zum Patchen: ${this.patchReadyCount} (${this.patchEntriesCount} Einträge)`);
      lines.push(`  - Bemerkungen-Cleanup: ${this.patchBemerkungenCleanCount} Bohrpunkte (${this.patchBemerkungenRemovedTotal} Arten extrahiert)`);
      lines.push(`  - Keine Änderungen nötig: ${this.patchNoChangesCount}`);
      lines.push(`  - Kein Match: ${this.patchNoMatchCount}`);
      lines.push(`  - Mehrdeutig: ${this.patchMultipleCount}`);
      lines.push(`  - Fehler: ${this.patchErrorCount}`);
      lines.push('');
      lines.push(`Gepatcht: ${this.patchedCount}`);
      lines.push(`Fehlgeschlagen: ${this.patchSkippedCount}`);
      lines.push('');
      for (const m of this.patchMatches) {
        const tag =
          m.applied === true ? '[OK]' :
          m.applied === false ? '[FEHLER]' :
          m.status === 'ready' ? '[BEREIT]' :
          m.status === 'no-changes' ? '[UNVER.]' :
          m.status === 'no-match' ? '[KEIN MATCH]' :
          m.status === 'multiple-matches' ? '[MEHRDEUTIG]' : '[FEHLER]';
        lines.push(`  ${tag} ${m.source}`);
        const dbNr = m.resolvedDoc?.header?.nummer ? ` → DB-Nr.: "${m.resolvedDoc.header.nummer}"` : '';
        lines.push(`         Excel-Nr.: "${m.nummer || '(leer)'}"${dbNr} — ${m.statusMessage}`);
        if (m.status === 'ready' || m.applied !== undefined) {
          for (const e of m.newEntries) {
            lines.push(`           + "${e.vegetation}" (${e.haeufigkeit ?? ''})`);
          }
          if (m.cleanedBemerkungen !== undefined) {
            lines.push(`           Bemerkungen bereinigt (${m.bemerkungenRemovedCount} Arten extrahiert)`);
          }
        }
        if (m.error) {
          lines.push(`         Fehler: ${m.error}`);
        }
      }
    }

    lines.push('');
    lines.push('='.repeat(60));
    lines.push('ENDE DES PROTOKOLLS');
    lines.push('='.repeat(60));

    // Datei erstellen und herunterladen
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-log_${now.toISOString().slice(0, 10)}_${now.toISOString().slice(11, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

}
